// Enum for document types
export enum DocumentType {
  ADHAR = "ADHAR",
  // Add other document types as needed
}
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { JOURNEY_EMAIL_REGEX } from "../../../utils/emailValidation";
import {
  Grid,
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "../../../services/redux/store";
import AddIcon from "@mui/icons-material/Add";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import postsServices from "../../../services/api/service/postsService";
import ModalPopup from "../../ui/ModalPopup";
import { PrimaryButton } from "../../ui/Button";
import { IconButton } from "../../ui/Button";
import { summaryActionButtonStyles } from "../../ui/commonCss";
import {
  removeAadhaarVerifiedDetailsCoApplicant,
  removeCoApplicantVerification,
  setAadhaarApplicationNoCoApplicant,
  setHorizontalSteps,
  updateFormData,
  removeIsDocumentVerifiedCoApplicant,
  removeAadhaarApplicationNoCoApplicant,
  setBreStatus,
  removeFormDataKeys,
  setActiveHorizontalIndex,
  setLoanDetailCoApplicantCollapseValue,
  LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY,
  setVerificationMessage,
  setDisableAddMoreCoApplicant,
  setSavedBasicForCoApplicant,
  setSavedAddressForCoApplicant,
  removeSavedBasicForCoApplicant,
  setEncryptionKey,
  setSummaryApplicationEditActive,
  setSummaryEditOpenedFromCoApplicantSection,
  setCoApplicantSummaryViewOnly,
  bumpFormJsonRefreshNonce,
  bumpCompletionSummaryRefreshNonce,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import CollapsibleSection from "../../../pages/common-features/CollapsibleSection";
import { SectionHeader } from "../../ui/SectionHeader";
import idIcon from "./../../../assets/svgIcons/id.svg";
import locationIcon from "./../../../assets/svgIcons/live-view.svg";
import RightBlackArrow from "./../../../assets/svgIcons/right-black-arrow.svg";
import VerifiedIcon from "./../../../assets/svgIcons/check-circle.svg";
import RemoveIcon from "./../../../assets/svgIcons/remove-icon.svg";
import LoaderBackdrop from "../../ui/LoaderBackdrop";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import applicantDetailsServices from "../../../services/api/service/applicantDetailsService";

import losServices from "../../../services/api/service/losService";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import { mapGender, handleOwnershipStatusValue, resolveBirthCityId } from "../../../utils/helpers";
import { trackCustomerJourneyEvent } from "../../../utils/ga";
import { decrypt, encryptData } from "../../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../../utils/piiKey";
import { getGAProvisionalOfferStatements, hasCoApplicantPayPartyChanged, getFetchedCoApplicantPayParty } from "./helper";
import { buildCoApplicantSaveIdDto, getMasterOptionName } from "./helperDto";
import { useApplicantMasterData } from "../../../hooks/useApplicantMasterData";
import * as Yup from "yup";
import { getValidationSchema } from "../../../utils/validationSchemas";
import { getFormJson } from "../../../services/redux/thunks/stepperDynamicFormThunks";

const isTruthyFormFieldRequired = (required: unknown) =>
  required === true ||
  required === "true" ||
  String(required ?? "").toLowerCase() === "yes";

const isTruthyFormFlag = (value: unknown): boolean =>
  value === true || value === "true" || value === 1 || value === "1";

const isCoApplicantSameAsAddressField = (field: any) =>
  (() => {
    const fieldName = String(field?.name || field?.key || "");
    return (
      !/^addressCoApplicant\d+currentsameAsPermanent$/i.test(fieldName) &&
      /^addressCoApplicant\d+(?:current)?sameAs(?:Permanent)?$/i.test(fieldName)
    );
  })();

const isCoApplicantFieldValueMissing = (f: any, val: any): boolean => {
  const t = f?.type;
  if (t === "checkbox") return val !== true;
  if (val === null || val === undefined) return true;
  if (t === "number") {
    return val === "" || (typeof val === "number" && Number.isNaN(val));
  }
  if (t === "date") {
    return val === "" || val == null;
  }
  if (typeof val === "string") return val.trim() === "";
  if (typeof val === "object") {
    const inner =
      (val as { value?: unknown }).value ??
      (val as { label?: unknown }).label;
    if (inner == null) return true;
    return String(inner).trim() === "";
  }
  return false;
};

const coApplicantEmploymentSaveExcluded = (
  fieldName: string,
  values: Record<string, any>,
  coAppNum: string
): boolean => {
  return !isCoApplicantEmploymentFieldVisible(fieldName, values, coAppNum);
};

const hasMeaningfulCoApplicantValue = (value: any) => {
  if (value === null || value === undefined || value === "") return false;
  if (value === false) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "object") {
    const inner = value?.value ?? value?.label;
    if (inner === null || inner === undefined) return false;
    return String(inner).trim() !== "";
  }
  return true;
};

const normalizeCoApplicantFieldValue = (value: any) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    const inner = value?.label ?? value?.value ?? "";
    return String(inner).trim();
  }
  return String(value).trim();
};

const isOtherRelationshipSelected = (value: any) => {
  const normalized = normalizeCoApplicantFieldValue(value).toLowerCase();
  return normalized === "other" || normalized === "others";
};

const isFarmerOrOtherEmploymentType = (value: any) => {
  const normalized = normalizeCoApplicantFieldValue(value).toLowerCase();
  return ["farmer", "other", "others"].includes(normalized);
};

function isCoApplicantEmploymentFieldVisible(
  fieldName: string,
  values: Record<string, any>,
  coAppNum: string
) {
  if (!new RegExp(`^employmentCoApplicant${coAppNum}`).test(fieldName)) {
    return true;
  }

  const isEmployedField = `employmentCoApplicant${coAppNum}isEmployed`;
  const employmentTypeField = `employmentCoApplicant${coAppNum}employmentType`;
  const isEmployed = normalizeCoApplicantFieldValue(values[isEmployedField])
    .toLowerCase();
  const employmentType = normalizeCoApplicantFieldValue(
    values[employmentTypeField]
  ).toLowerCase();

  if (fieldName === isEmployedField) return true;
  if (isEmployed !== "yes") return false;
  if (fieldName === employmentTypeField) return true;
  if (!employmentType) return false;

  if (isFarmerOrOtherEmploymentType(employmentType)) {
    return (
      fieldName === `employmentCoApplicant${coAppNum}monthlyObligation` ||
      fieldName === `employmentCoApplicant${coAppNum}monthlyIncome`
    );
  }

  return true;
}

function isCoApplicantEmploymentFieldRequired(
  field: any,
  values: Record<string, any>,
  coAppNum: string
) {
  const fieldName = field?.name || "";
  if (!new RegExp(`^employmentCoApplicant${coAppNum}`).test(fieldName)) {
    return isTruthyFormFieldRequired(field?.required);
  }
  const employmentType =
    values?.[`employmentCoApplicant${coAppNum}employmentType`];
  if (
    fieldName === `employmentCoApplicant${coAppNum}designation` &&
    isFarmerOrOtherEmploymentType(employmentType)
  ) {
    return false;
  }
  return isCoApplicantEmploymentFieldVisible(fieldName, values, coAppNum);
}

function withCoApplicantEmploymentRequiredState(
  field: any,
  values: Record<string, any>,
  coAppNum: string
) {
  if (!field?.name || !new RegExp(`^employmentCoApplicant${coAppNum}`).test(field.name)) {
    return field;
  }
  return {
    ...field,
    required: isCoApplicantEmploymentFieldRequired(field, values, coAppNum),
  };
}

type EmploymentTypeOption = "Salaried" | "SEP" | "SENP" | "Student" | "Others";
  type GenderOption = { code: string; name: string };

const CoApplicantDetailsSection = ({
  fields,
  renderField,
  values,
  formikRef,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  pinCodeData,
  dropdownData,
  handleInvokZipCode,
  relationshipOptionsList,
  salutationOptionsList,
  zipCodeLoading,
  tabIndex,
  setTabIndex,
  checkIsLeadApiInvoked,
  handleLeadCreationFlow,
  getLeadPayload,
  pageType,
  selectedCoApplicantKey,
}: any) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    nationalityOptions: nationalityMasterOptions,
    maritalStatusOptions: maritalStatusMasterOptions,
    countryOptions: birthCountryMasterOptions,
    fetchCitiesByCountryState,
    getCityOptionsForCountry,
    loadMasterOptions,
  } = useApplicantMasterData();
  const {
    templateId,
    applicationId,
    applicantId,
    trackingId,
    isDocumentVerifiedCoApplicant,
    coApplicantZipCodeData,
    formData,
    coApplicantVerification,
    disableAddMoreCoApplicant,
    savedBasicCoApplicants,
    savedAddressCoApplicants,
    loanDetailCoApplicantCollapseValue,
    encryptionKey,
    summaryEditOpenedFromCoApplicantSection,
    fetchUserApplicationData,
  } = useSelector((state: RootState) => state.stepper);
  const applicationSource = useSelector((state: RootState) => state?.stepper?.applicationSource);
  const leadSequenceId = useSelector((state: RootState) => state?.stepper?.leadSequenceId);

  const { userProfile } = useSelector((state: RootState) => state.auth);
  const notInList = useSelector(
    (state: RootState) => state.stepper.notInList ?? []
  );

  const handleGAProvisionalOfferStatments = useMemo(
    () => getGAProvisionalOfferStatements(applicationSource),
    [applicationSource]
  );

  const fetchSanitizedPiiPublicKey = useCallback(async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";

    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));

    return getSanitizedPublicKeyFromResponse(rawKey);
  }, [encryptionKey, dispatch]);

  const syncAddMoreStateFromCurrentFields = useCallback(() => {
    const currentValues = formikRef?.current?.values || {};
    const employmentFields = (fields || []).filter((field: any) =>
      /^employmentCoApplicant\d+isEmployed$/.test(field?.name || "")
    );

    if (employmentFields.length === 0) {
      dispatch(setDisableAddMoreCoApplicant(true));
      return;
    }

    const validEmploymentFields = employmentFields.filter((field: any) => {
      const match = String(field?.name || "").match(
        /^employmentCoApplicant(\d+)isEmployed$/
      );
      const coAppNum = match?.[1];
      const isEmployed = currentValues?.[field?.name];
      if (String(isEmployed ?? "").trim().toLowerCase() !== "yes") {
        return false;
      }
      if (!coAppNum) return false;
      const employmentType =
        currentValues?.[`employmentCoApplicant${coAppNum}employmentType`];
      return String(employmentType ?? "").trim() !== "";
    });

    dispatch(setDisableAddMoreCoApplicant(validEmploymentFields.length === 0));
  }, [dispatch, fields, formikRef]);

  // Validate Redux fields on component mount
  useEffect(() => {
    if (pageType === "SUMMARY") return;
    syncAddMoreStateFromCurrentFields();
  }, [pageType, syncAddMoreStateFromCurrentFields]);

  // Track whether auto-initialization of co-applicant fields has already been done
  const autoInitRef = React.useRef(false);
  const summaryAddInitRef = React.useRef<string | null>(null);
  const defaultExpandedInitializedRef = React.useRef(false);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaveNextLoading, setIsSaveNextLoading] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: "error" | "success";
  }>({
    open: false,
    message: "",
    severity: "error",
  });
  const shouldShowLoanFields =
    !(pageType === "SUMMARY" && summaryEditOpenedFromCoApplicantSection);
  const shouldShowCoApplicantSection =
    !(pageType === "SUMMARY" && !summaryEditOpenedFromCoApplicantSection);
  // Aadhaar verification state per co-applicant
  const [isAadharLoading, setIsAadharLoading] = useState<
    Record<string, boolean>
  >({});
  const coAppNums = fields
    .map((f: any) => {
      const match = f.name.match(/^(basic|address|employment)CoApplicant(\d+)/);
      return match ? parseInt(match[2], 10) : null;
    })
    .filter((n: number | null) => n !== null);
  const numberOfCoApp = Object.keys(
    coAppNums.reduce((acc: any, curr: any) => {
      acc[curr] = true;
      return acc;
    }, {} as Record<number, boolean>)
  ).length;
// Collapsible state
  const [expanded, setExpanded] = React.useState<string | false>(
    false
  );
  // Calculate next co-applicant number based on field names (support all prefixes)
  // Master options sourcing for dynamic co-applicant fields
  const relationshipOptions =
    fields?.find((f: any) => f?.name?.includes("RelationWithStudent"))?.options
      ?.options ?? [];
  const payPartyOptions =
    fields?.find((f: any) => f?.name?.includes("PayParty"))?.options?.options ??
    [];
  const permanentOwnershipStatusOptions =
    fields?.find((f: any) => f?.name?.includes("permanentOwnershipStatus"))
      ?.options?.options ?? [];
  const currentOwnershipStatusOptions =
    fields?.find((f: any) => f?.name?.includes("currentOwnershipStatus"))
      ?.options?.options ?? [];
  const isEmployedOptions =
    fields?.find((f: any) => f?.name?.includes("isEmployed"))?.options
      ?.options ?? [];
  const employmentTypeOptions: EmploymentTypeOption[] =
    fields?.find((f: any) => f?.name?.includes("employmentType"))?.options
      ?.options ?? [];
  const designationOptions =
    fields?.find((f: any) => {
      const name = String(f?.name || "").toLowerCase();
      return (
        name.includes("designation") &&
        Array.isArray(f?.options?.options) &&
        f.options.options.length > 0
      );
    })?.options?.options ??
    dropdownData?.designations ??
    [];
  const fallbackGenderOptions: GenderOption[] = mapGender();
  const genderSourceField = fields?.find((f: any) => {
    const name = String(f?.name || "").toLowerCase();
    return name.includes("gender") && !name.includes("verified");
  });
  const genderOptions: GenderOption[] =
    genderSourceField?.options?.options?.length
      ? genderSourceField.options.options
      : fallbackGenderOptions;
  const nationalityOptions =
    fields?.find((f: any) => f?.name?.includes("Nationality"))?.options ?? {
      options: nationalityMasterOptions,
    };
  const maritalStatusOptions =
    fields?.find((f: any) => f?.name?.includes("MaritalStatus"))?.options
      ?.options ?? maritalStatusMasterOptions;

  const updateAddMoreStateFromLocalValues = () => {
    const currentValues = formikRef?.current?.values || {};
    const isEmployedFields = (fields || []).filter(
      (item: any) =>
        item?.name?.includes("employmentCoApplicant") &&
        item?.name?.endsWith("isEmployed")
    );

    const hasAnyBlank = isEmployedFields.some((item: any) => {
      const fieldValue = currentValues[item.name];
      return (
        fieldValue === "" ||
        fieldValue === null ||
        fieldValue === undefined
      );
    });

    dispatch(setDisableAddMoreCoApplicant(hasAnyBlank));
  };

  // Get the current vertical step object
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
    activeVerticalIndex
    ];
  const currentFormValues = values || formikRef?.current?.values || {};

  // Get the vertical step id
  const verticalStepId = currentVerticalStep?.id;
  const selectedCoApplicantNumber = (() => {
    if (
      pageType !== "SUMMARY" ||
      !selectedCoApplicantKey ||
      selectedCoApplicantKey === LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY
    ) {
      return null;
    }
    const match = String(selectedCoApplicantKey).match(/^CoApplicant(\d+)$/);
    return match ? Number(match[1]) : null;
  })();
  const hasFieldsForSelectedCoApplicant =
    selectedCoApplicantNumber == null
      ? true
      : fields?.some((field: any) =>
          new RegExp(
            `^(basic|address|employment)CoApplicant${selectedCoApplicantNumber}`
          ).test(field?.name || "")
        );

  const coApplicantDisplayFields = useMemo(() => {
    const byName = new Map<string, any>();
    (fields || []).forEach((field: any) => {
      const name = String(field?.name || "");
      if (!/^(basic|address|employment)CoApplicant\d+/.test(name)) return;

      const existing = byName.get(name);
      if (!existing) {
        byName.set(name, field);
        return;
      }

      const currentValue =
        formikRef?.current?.values?.[name] ?? field?.value ?? formData?.[name];
      const existingValue =
        formikRef?.current?.values?.[name] ??
        existing?.value ??
        formData?.[name];
      if (
        hasMeaningfulCoApplicantValue(currentValue) &&
        !hasMeaningfulCoApplicantValue(existingValue)
      ) {
        byName.set(name, field);
      }
    });
    return Array.from(byName.values());
  }, [fields, formData, formikRef]);

  const currentCoApplicantNums = useMemo(() => {
    const nums = (coApplicantDisplayFields || [])
      .map((field: any) => {
        const match = String(field?.name || "").match(
          /^(basic|address|employment)CoApplicant(\d+)/
        );
        return match ? Number(match[2]) : null;
      })
      .filter((value: number | null): value is number => value !== null);
    return Array.from(new Set<number>(nums)).sort((a: number, b: number) => a - b);
  }, [coApplicantDisplayFields]);

  // Auto-initialize co-applicant fields on mount if no new-format fields exist
  // This ensures every co-applicant gets basic + address + employment sections
  useEffect(() => {
    // Skip if already initialized, or missing required data
    if (autoInitRef.current) return;
    if (pageType === "SUMMARY") return;
    if (!verticalStepId) return;
    if (!trackingId) return;
    if (!relationshipOptions?.length && !salutationOptionsList?.length) {
      // Wait for options to be loaded before initializing
      return;
    }
    if (activeHorizontalIndex === 1 && activeVerticalIndex === 5 && trackingId) {
      return;
    }

    // Check if fields already have new-format co-applicant fields
    const hasNewFormatFields =
      Array.isArray(fields) &&
      fields.some((f: any) => {
        const name = f?.name || "";
        return /^(basic|address|employment)CoApplicant\d+/.test(name);
      });

    // Only auto-initialize if no new-format fields exist
    if (!hasNewFormatFields) {
      autoInitRef.current = true;
      addCoApplicantFields({
        source: "initial",
      });
    }
  }, [
    activeHorizontalIndex,
    activeVerticalIndex,
    pageType,
    verticalStepId,
    trackingId,
    relationshipOptions,
    salutationOptionsList,
    fields,
    currentCoApplicantNums,
  ]);

  const addCoApplicantFields = async (options?: {
    coApplicantNum?: number;
    source?: "add-more" | "initial";
  }) => {
    if (options?.source === "add-more") {
      dispatch(setDisableAddMoreCoApplicant(true));
    }
    setIsLoading(true);
    try {
      let nextCoApplicantNum = options?.coApplicantNum ?? selectedCoApplicantNumber ?? 0;
      if (!nextCoApplicantNum) {
        const existingMaxCoApplicantNum = currentCoApplicantNums.length
          ? Math.max(...currentCoApplicantNums)
          : 0;
        let apiNextCoApplicantNum = 0;
        try {
          const countResp = await postsServices.addPost(
            "api/v1/applicant/get-coapplicant-count",
            { trackingId }
          );
          const apiCount = countResp?.data?.data?.count;
          if (typeof apiCount === "number") {
            apiNextCoApplicantNum = apiCount + 1;
          }
        } catch (countErr) {
          console.warn("Unable to fetch latest co-applicant count", countErr);
        }
        const fallbackNextCoApplicantNum = existingMaxCoApplicantNum + 1 || 1;
        nextCoApplicantNum = Math.max(
          apiNextCoApplicantNum,
          fallbackNextCoApplicantNum
        );
      }

      if (
        fields?.some((field: any) =>
          new RegExp(
            `^(basic|address|employment)CoApplicant${nextCoApplicantNum}`
          ).test(field?.name || "")
        )
      ) {
        setExpanded(`CoApplicant${nextCoApplicantNum}`);
        return;
      }

      setExpanded(`CoApplicant${nextCoApplicantNum}`);

      const apiFields = [
        // Basic Details
        {
          label: "Relationship with Student",
          name: `basicCoApplicant${nextCoApplicantNum}RelationWithStudent`,
          type: "autocomplete",
          required: true,
          options: { options:relationshipOptions },
        },
        {
          label: "Specify Relationship",
          name: `basicCoApplicant${nextCoApplicantNum}OtherRelationWithStudent`,
          required: false,
          type: "text",
          options: null,
        },
        {
          label: "How should we address you? (Mr./Ms./etc.)",
          name: `basicCoApplicant${nextCoApplicantNum}salutation`,
          type: "dropdown",
          required: true,
          options: {
            options: salutationOptionsList,
          },
        },
        {
          label: "Co-Applicant First Name",
          name: `basicCoApplicant${nextCoApplicantNum}FirstName`,
          type: "text",
          required: true,
        },
        {
          label: "Co-Applicant Last Name",
          name: `basicCoApplicant${nextCoApplicantNum}LastName`,
          type: "text",
          required: true,
        },
        {
          label: "Gender",
          name: `basicCoApplicant${nextCoApplicantNum}Gender`,
          type: "dropdown",
          options: { options: genderOptions },
          required: true,
        },
        {
          label: "Date of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}dob`,
          type: "date",
          required: true,
        },
        {
          label: "PAN Number",
          name: `basicCoApplicant${nextCoApplicantNum}PanNumber`,
          type: "text",
          required: true,
        },
        {
          label: "Co-applicant mobile number",
          name: `basicCoApplicant${nextCoApplicantNum}PhoneNumber`,
          type: "phone",
          required: true,
        },
        {
          label: "Co-applicant email id",
          name: `basicCoApplicant${nextCoApplicantNum}Email`,
          type: "email",
          required: true,
        },
        {
          label: "PAN Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsPanVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Mobile Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsMobileVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Email Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsEmailVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Nationality",
          name: `basicCoApplicant${nextCoApplicantNum}Nationality`,
          type: "autocomplete",
          required: true,
          options:nationalityOptions,
        },
        {
          label: "Marital Status",
          name: `basicCoApplicant${nextCoApplicantNum}MaritalStatus`,
          type: "dropdown",
          required: true,
          options: { options: maritalStatusOptions },
        },
        {
          label: "Country of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}CountryOfBirth`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "City of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}CityOfBirth`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Aadhaar Verified",
          name: `basicCoApplicant${nextCoApplicantNum}AadhaarVerified`,
          type: "boolean",
          required: false,
        },
        {
          label:
            "Do you want to consider income of the co-applicant for loan evaluation?",
          name: `basicCoApplicant${nextCoApplicantNum}PayParty`,
          type: "radio",
          required: true,
          options: { options: payPartyOptions },
        },

        // Address Details (Permanent and Current)
        {
          label: "Owner Ship Status",
          name: `addressCoApplicant${nextCoApplicantNum}permanentOwnershipStatus`,
          type: "dropdown",
          required: true,
          options: { options: permanentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: `addressCoApplicant${nextCoApplicantNum}permanentYearsInCurrentAddress`,
          type: "number",
          required: true,
        },
        {
          label: "How long have you lived in this city ",
          name: `addressCoApplicant${nextCoApplicantNum}permanentYearsInCurrentCity`,
          type: "number",
          required: true,
        },
        {
          label: "Address Line 1",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine1`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine3`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `addressCoApplicant${nextCoApplicantNum}permanentPincode`,
          type: "text",
          required: true,
        },
        {
          label: "City",
          name: `addressCoApplicant${nextCoApplicantNum}permanentCity`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: `addressCoApplicant${nextCoApplicantNum}permanentState`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: `addressCoApplicant${nextCoApplicantNum}permanentCountry`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Keep Current Address same as Permanent Address",
          name: `addressCoApplicant${nextCoApplicantNum}currentsameAsPermanent`,
          type: "checkbox",
          required: false,
        },
        {
          label: "Owner Ship Status",
          name: `addressCoApplicant${nextCoApplicantNum}currentOwnershipStatus`,
          type: "dropdown",
          required: true,
          options: { options: currentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: `addressCoApplicant${nextCoApplicantNum}currentYearsInCurrentAddress`,
          type: "text",
          required: true,
        },
        {
          label: "How long have you lived in this city",
          name: `addressCoApplicant${nextCoApplicantNum}currentYearsInCurrentCity`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 1",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine1`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine3`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `addressCoApplicant${nextCoApplicantNum}currentPincode`,
          type: "text",
          required: true,
        },
        {
          label: "City",
          name: `addressCoApplicant${nextCoApplicantNum}currentCity`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: `addressCoApplicant${nextCoApplicantNum}currentState`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: `addressCoApplicant${nextCoApplicantNum}currentCountry`,
          type: "autocomplete",
          required: true,
        },

        // Employment Details
        {
          label: "Are you employed?",
          name: `employmentCoApplicant${nextCoApplicantNum}isEmployed`,
          type: "radio",
          required: true,
          options: { options: isEmployedOptions },
        },
        {
          label: "Employment Type",
          name: `employmentCoApplicant${nextCoApplicantNum}employmentType`,
          type: "dropdown",
          required: true,
          options: { options: employmentTypeOptions },
        },
        {
          label: "Monthly Obligation",
          name: `employmentCoApplicant${nextCoApplicantNum}monthlyObligation`,
          type: "number",
          required: false,
        },
        {
          label: "Company Name",
          name: `employmentCoApplicant${nextCoApplicantNum}companyName`,
          type: "text",
          required: false,
        },
        {
          label: "Designation",
          name: `employmentCoApplicant${nextCoApplicantNum}designation`,
          type: "autocomplete",
          required: false,
        },
        {
          label: "Monthly Income",
          name: `employmentCoApplicant${nextCoApplicantNum}monthlyIncome`,
          type: "number",
          required: false,
        },
        {
          label: "Year",
          name:  `employmentCoApplicant${nextCoApplicantNum}employmentYear`,
          required: false,
          type: "number"
        },
        {
          label: "Month",
          name:`employmentCoApplicant${nextCoApplicantNum}employmentMonth`,
          type: "number",
          required: false,
        },
        // ── Company Address — FIX 8: using companyField helper ──
        {
          label: "Address Line 1",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress1`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 2",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress3`,
          type: "text",
          required: false,
        },
        {
          label: "Landmark",
          name: `employmentCoApplicant${nextCoApplicantNum}landmark`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `employmentCoApplicant${nextCoApplicantNum}pincode`,
          type: "number",
          required: false,
        },
        {
          label: "City",
          name:`employmentCoApplicant${nextCoApplicantNum}city`,
          type: "text",
          required: false,
        },
        {
          label: "State",
          name: `employmentCoApplicant${nextCoApplicantNum}state`,
          type: "text",
          required: false,
        },
        {
          label: "Country",
          name: `employmentCoApplicant${nextCoApplicantNum}companyCountry`,
          type: "autocomplete",
          required: false,
        },

      ];
      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          trackingId: trackingId,
          fields: apiFields,
        }
      );
      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        // --- PUSH new fields into the correct vertical step (id: 9, Loan Details) in applicant_loan horizontal step in Redux state ---
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          console.log("applicantLoanStepIdx hori state", state.stepper.horizontalSteps)
          const horizontalSteps = [...state.stepper.horizontalSteps];

          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );

          console.log("applicantLoanStepIdx: stepx", applicantLoanStepIdx);
          console.log("applicantLoanStepIdx hori", horizontalSteps)

          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Instead of hardcoding id === 9, use the current vertical step (activeVerticalIndex)
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update using setHorizontalSteps action creator
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Always overwrite Formik values for the new co-applicant fields to avoid stale data
        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            let defaultValue: any = "";

            if (
              field.type === "dropdown" &&
              field.options?.options?.length > 0
            ) {
              // defaultValue =
              //   field.options.options[0]?.value ??
              //   field.options.options[0] ??
              //   "";
              defaultValue = ""; // Always empty by default
            } else if (field.type === "boolean") {
              defaultValue = false;
            }
            // Always overwrite, even if Formik already has a value for this key
            formikRef.current.setFieldValue(field.name, defaultValue, false);
          });
        }
      }
    } catch (err) {
      console.error("Add entry failed", err);
      if (options?.source === "add-more") {
        dispatch(setDisableAddMoreCoApplicant(false));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pageType !== "SUMMARY") return;
    if (!selectedCoApplicantNumber) return;
    if (hasFieldsForSelectedCoApplicant) return;
    if (!verticalStepId || !trackingId) return;
    if (summaryAddInitRef.current === selectedCoApplicantKey) return;

    summaryAddInitRef.current = selectedCoApplicantKey;
    addCoApplicantFields({
      coApplicantNum: selectedCoApplicantNumber,
      source: "initial",
    });
  }, [
    pageType,
    selectedCoApplicantNumber,
    hasFieldsForSelectedCoApplicant,
    verticalStepId,
    trackingId,
    selectedCoApplicantKey,
  ]);

  const removeCoApplicantFields = async (fieldIds: number[]) => {
    dispatch(setDisableAddMoreCoApplicant(false));
    // Extract co-applicant index and number from field names (e.g., "coApplicant2Name" -> "1" for index '1')
    let coAppIndex: string | null = null;
    let coAppNum: string | null = null;
    const allFields = Array.isArray(fields)
      ? fields[0]?.fields
        ? fields.flatMap((g: any) => g.fields)
        : fields
      : [];

    for (const field of allFields) {
      if (fieldIds.includes(field.id)) {
        const match = field.name.match(
          /^(basic|address|employment)CoApplicant(\d+)/
        );

        if (match) {
          coAppIndex = String(Number(match[2]) - 1);
          coAppNum = match[2];
          break;
        }
      }
    }

    const removedCoApplicantKey = coAppNum ? `CoApplicant${coAppNum}` : "";
    const removedActiveSummaryCoApplicant =
      pageType === "SUMMARY" &&
      selectedCoApplicantKey &&
      removedCoApplicantKey &&
      selectedCoApplicantKey === removedCoApplicantKey;
    let deleteUserFieldsPromise: Promise<any> | null = null;

    // If basic details for this co-applicant were previously saved, call delete-coapplicant API
    if (coAppNum) {
      const coAppKey = removedCoApplicantKey;
      const coApplicantType = `Co-Applicant ${coAppNum}`;
      let processioApplicantId = null;
      const basicRequiredFields = allFields
        .filter(
          (field: any) =>
            new RegExp(`^basicCoApplicant${coAppNum}`, "i").test(field?.name) &&
            !/^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|IsPanVerified)$/i.test(field?.name || "") &&
            isTruthyFormFieldRequired(field?.required)
        );
      const basicRequiredFilled =
        basicRequiredFields.length > 0 &&
        basicRequiredFields.every(
          (field: any) =>
            !isCoApplicantFieldValueMissing(
              field,
              formikRef?.current?.values?.[field?.name]
            )
        );
      if (savedBasicCoApplicants?.[coAppKey] || basicRequiredFilled) {
        try {
          const coApplicantResp = await losServices.getCoApplicantDetails({
            trackingId,
            coApplicantType,
          });
          processioApplicantId = coApplicantResp?.data?.data?.applicantId;
        } catch (err) {
          console.error("Error fetching co-applicant details before delete", err);
        }
        try {
          const deletePayload = {
            trackingId: trackingId,
            coApplicantType,
          };
          await losServices.deleteCoApplicant(deletePayload);
          // cleanup redux flag after successful delete
          if (savedBasicCoApplicants?.[coAppKey]) {
            dispatch(removeSavedBasicForCoApplicant(coAppKey));
          }
        } catch (err: any) {
          setSnackbar({
            open: true,
            message:
              err?.response?.data?.message ||
              "Unable to delete co-applicant.",
            severity: "error",
          });
          console.error("Error calling delete-coapplicant API", err);
          return;
        }
      }
     
    }

    // Remove verification state for this co-applicant
    if (coAppIndex !== null) {
      dispatch(removeCoApplicantVerification(coAppIndex));
    }
    // Remove Aadhaar verified details for this co-applicant from Redux
    if (coAppNum) {
      const coAppKey = `CoApplicant${coAppNum}`;
      dispatch(removeAadhaarVerifiedDetailsCoApplicant(coAppKey));
      dispatch(removeIsDocumentVerifiedCoApplicant(coAppKey));
      dispatch(removeAadhaarApplicationNoCoApplicant({ coAppNum, coAppKey }));

      // Remove all formData keys for this co-applicant from Redux and Formik
      if (formikRef?.current) {
        const values = { ...formikRef?.current?.values };
        const keysToRemove: string[] = [];
        // Remove all keys for this co-applicant (including empty/false values)
        const coAppRegex = new RegExp(
          `^(basic|address|employment)CoApplicant${coAppNum}`
        );
        Object.keys(values).forEach((key) => {
          if (coAppRegex.test(key)) {
            keysToRemove.push(key);
            delete values[key];
          }
        });
        if (keysToRemove.length > 0) {
          dispatch(removeFormDataKeys(keysToRemove));
        }
        // Also remove from Formik values
        formikRef?.current.setValues(values, false);
        formikRef?.current.validateForm();
      }
    }
    try {
      const resp = await (
        deleteUserFieldsPromise ||
        postsServices.addPost("api/v1/product/delete-user-fields", {
          form_field_ids: fieldIds,
          trackingId: trackingId,
        })
      );

      if (resp.data?.status === "success") {
        // Remove from Redux: Remove fields from horizontalSteps (vertical step id: 9 in applicant_loan)
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );
          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Use the current vertical step (activeVerticalIndex) instead of hardcoded id === 9
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds.includes(field.id)
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Find field names to clear BEFORE removing from Redux
        const fieldNamesToClear: string[] = [];
        fields && fields?.forEach((group: any) => {
          const groupFields = Array.isArray(group?.fields) ? group.fields : [group];
          groupFields?.forEach((field: any) => {
            if (fieldIds.includes(field.id)) {
              fieldNamesToClear.push(field.name);
            }
          });
        });

        // Remove all keys for this co-applicant from Formik values ONLY, do not setFieldValue for those fields after
        if (formikRef?.current && coAppNum !== null) {
          const prefixRegex = new RegExp(
            `^(basic|address|employment)CoApplicant${coAppNum}`
          );
          const newValues = { ...formikRef.current.values };
          Object.keys(newValues).forEach((key) => {
            if (prefixRegex.test(key)) {
              delete newValues[key];
            }
          });
          formikRef.current.setValues(newValues, false);
          // Force Formik to re-validate and re-render
          formikRef.current.validateForm();
        } else if (formikRef?.current) {
          // If not a co-applicant, just clear the fields as before
          fieldNamesToClear.forEach((name) => {
            const isBoolean =
              name?.toLowerCase().includes("is") ||
              name?.toLowerCase().includes("verified");
            formikRef.current.setFieldValue(
              name,
              isBoolean ? false : "",
              false
            );
          });
        }

        if (removedActiveSummaryCoApplicant) {
          dispatch(setLoanDetailCoApplicantCollapseValue(""));
          dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
          dispatch(setSummaryApplicationEditActive(false));
          setExpanded(false);
        } else if (removedCoApplicantKey && expanded === removedCoApplicantKey) {
          setExpanded(false);
        }

        dispatch(setDisableAddMoreCoApplicant(false));
      }
    } catch (err) {
      console.error("Error deleting coapplicant fields", err);
    } finally {
      if (trackingId) {
        dispatch(bumpFormJsonRefreshNonce());
        dispatch(bumpCompletionSummaryRefreshNonce());
        dispatch(getFormJson({ trackingId: String(trackingId), loaderShow: false }));
      }
    }
  };

  // Handler for remove button click
  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };

  // Handler for confirm in modal
  const handleConfirmRemove = () => {
    if (pendingRemoveIds !== null) {
      removeCoApplicantFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // Handler for cancel in modal
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  const handleCancelSummaryEdit = () => {
    dispatch(setLoanDetailCoApplicantCollapseValue(""));
    dispatch(setCoApplicantSummaryViewOnly(false));
    dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
    dispatch(setSummaryApplicationEditActive(false));
    setExpanded(false);
  };

  const handleProceedClick = async () => {
    dispatch(setBreStatus("FROM_FORM"));

    // Only send LoanDetailsLoanAmount and collateral fields, and if collateral is 'Yes', also collateralType and collateralAmount
    const data: any[] = [];
    const values = formikRef?.current?.values || {};
    const collateralValue = values["collateral"];
    fields.forEach((field: any) => {
      if (
        field.name === "LoanDetailsLoanAmount" ||
        field.name === "collateral" ||
        (collateralValue === "Yes" &&
          (field.name === "collateralType" ||
            field.name === "collateralAmount"))
      ) {
        data.push({
          formFieldId: field.id,
          formFieldKey: field.name,
          formFieldValue: values[field.name] ?? "",
        });
      }
    });
    // Build payload for createApplicant API
    const payloadObject = {
      data,
      trackingId: trackingId,
      templateId: templateId,
      formName: horizontalSteps?.[activeHorizontalIndex]?.stepLabel,
      mobileNumber: values?.phone_number,
    };

    try {
      dispatch(updateFormData(values));
      await applicantDetailsServices
        .createApplicant(payloadObject)
        .then(() => {

          trackCustomerJourneyEvent(
            `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
            decrypt(userProfile?.id) ?? "",
            trackingId,
            {
              label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              page: "ProvisionalOff_Loan Details_Provisional Offer",
            }
          );

          dispatch(setActiveHorizontalIndex(activeHorizontalIndex + 1));
          // dispatch(nextVerticalStep());
        })
        .catch((err) => {
          const apiErrorMessage = err.response?.data?.message || "";
          let displayMessage = "Something went wrong while saving data.";
          if (apiErrorMessage.includes("LoanDetailsLoanAmount")) {
            displayMessage = "Please enter a loan amount that matches our lending policy to proceed.";
          } else if (apiErrorMessage) {
            displayMessage = apiErrorMessage;
          }

          setSnackbar({
            open: true,
            message: displayMessage,
            severity: "error",
          });
        });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong while saving data.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (!formikRef?.current || !formikRef?.current?.values) return;

    const currentFieldNames = fields?.map((field: any) => field?.name);
    const allValues = { ...formikRef?.current?.values };

      // Find all co-applicant keys in Formik
      const coApplicantKeys = Object.keys(allValues).filter((key) =>
        key.startsWith("coApplicant")
      );

      // Find co-applicant keys that are NOT in the current fields
      const keysToRemove = coApplicantKeys.filter(
        (key) => !currentFieldNames.includes(key)
      );

      // Remove only those keys
      keysToRemove.forEach((key) => {
        delete allValues[key];
      });

      // Add any missing current co-applicant fields with default ""
      currentFieldNames.forEach((name: any) => {
        if (!(name in allValues)) {
          // Set employmentCoApplicantNisEmployed to 'Yes' by default
          if (/^employmentCoApplicant\d+isEmployed$/.test(name)) {
            allValues[name] = "Yes";
        } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
            allValues[name] = false;
          } else {
            allValues[name] = "";
          }
      } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
        // Always coerce to boolean, never string
        allValues[name] = allValues[name] === true || allValues[name] === "true";
        }
      });

      // Always set basicCoApplicant1PayParty to 'Yes' for co-applicant 1
      if (currentFieldNames.includes("basicCoApplicant1PayParty")) {
        allValues["basicCoApplicant1PayParty"] = "Yes";
      }

      formikRef.current.setValues(allValues, false);
  }, [fields]);

  // Only set addressCoApplicantNcurrentsameAsPermanent to false if undefined (not if user checked it)
  useEffect(() => {
    if (formikRef?.current) {
      const allFieldNames = Object.keys(formikRef?.current?.values || {});
      const coApplicantFields = allFieldNames.filter((name) =>
        name.match(/^addressCoApplicant(\d+)currentsameAsPermanent$/)
      );
      coApplicantFields.forEach((fieldName) => {
        const value = formikRef?.current?.values[fieldName];
        if (typeof value === "undefined" || value === "" || value === null) {
          formikRef?.current.setFieldValue(fieldName, false, false);
        }
      });
    }
  }, [fields]);

	  useEffect(() => {
	    if (formikRef?.current) {
	      const values = formikRef?.current?.values || {};
	      let shouldUpdate = false;
	      if (Array.isArray(fields)) {
	        fields.forEach((field) => {
	          if (
	            /^employmentCoApplicant\d+isEmployed$/.test(field.name) &&
	            (!values[field.name] || values[field.name] === "")
	          ) {
	            formikRef?.current?.setFieldValue(field.name, "Yes", false);
	            shouldUpdate = true;
	          }
	        });
	      }
	    }
	  }, [fields, formikRef]);

	  useEffect(() => {
	    if (formikRef?.current) {
	      formikRef.current.setFieldValue(
	        "employmentCoApplicant1isEmployed",
	        "Yes",
	        false
	      );
	    }
	  }, [fields, formikRef]);

  return (
    <>
      {/* Set default value to 'No' for collateral on mount */}
      {(() => {
        if (formikRef?.current && formikRef.current.values) {
          if (
            formikRef.current.values["collateral"] === undefined ||
            formikRef.current.values["collateral"] === ""
          ) {
            formikRef.current.setFieldValue("collateral", "No", false);
          }
        }
      })()}
      {/* Only Loan Amount and Collateral fields */}
      {shouldShowLoanFields && (
        <Grid container spacing={2} m={0}>
        <Grid item xs={12} md={4}>
            {fields
              .filter((f: any) => f.name === "LoanDetailsLoanAmount")
              .map((field: any) => (
                <React.Fragment key={field.name}>
                  {renderField(field)}
                </React.Fragment>
              ))}
          </Grid>
        <Grid item xs={12} md={4}>
            {fields
              .filter((f: any) => f.name === "collateral")
              .map((field: any) => (
                <React.Fragment key={field.name}>
                  {renderField(field)}
                </React.Fragment>
              ))}
          </Grid>
        <Grid item xs={12}>
          {/* If collateral is 'Yes', show collateralType and collateralAmount fields side by side and make them required */}
          {formikRef?.current?.values?.["collateral"] === "Yes" && (
            <Grid container spacing={2}>
              {fields
                .filter(
                  (f: any) =>
                    f.name === "collateralType" || f.name === "collateralAmount"
                )
                .map((field: any) => {
                  // If collateral is 'Yes', force required true for these fields
                  const fieldWithRequired = {
                    ...field,
                    required: true,
                  };
                  return (
                    <Grid item xs={12} sm={4} key={field.name}>
                      {renderField(fieldWithRequired)}
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </Grid>
        </Grid>
      )}

      {/* Co-Applicant Details Header */}
      {pageType !== "SUMMARY" && (
        <Grid container spacing={2} justifyContent="space-between" mx={0} mt={1}>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "15px" }}>
              Co-Applicant Details
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Render each co-applicant in a collapsible Accordion with tabs */}
      {shouldShowCoApplicantSection && (() => {
        // Group all co-applicant fields by co-applicant number and prefix
        const coApplicantFieldMap: Record<
          string,
          { basic: any[]; address: any[]; employment: any[]; ids: number[] }
        > = {};
        console.log(coApplicantDisplayFields, 'coApplicantDisplayFields employmentFields')
        coApplicantDisplayFields.forEach((field: any) => {
          const match = field.name.match(
            /^(basic|address|employment)CoApplicant(\d+)/
          );
          if (match) {
            const prefix = match[1] as "basic" | "address" | "employment";
            const num = match[2];
            const key = `CoApplicant${num}`;
            if (!coApplicantFieldMap[key]) {
              coApplicantFieldMap[key] = {
                basic: [],
                address: [],
                employment: [],
                ids: [],
              };
            }
            coApplicantFieldMap[key][prefix].push(field);
            if (field.id) {
              coApplicantFieldMap[key].ids.push(field.id);
            }
          }
        });

        // Sort co-applicants by number
        const coApplicantKeys = Object.keys(coApplicantFieldMap).sort(
          (a, b) => {
            const aNum = parseInt(a.replace("CoApplicant", ""), 10);
            const bNum = parseInt(b.replace("CoApplicant", ""), 10);
            return aNum - bNum;
          }
        );
        // Sync local expanded only for an active summary edit target.
        React.useEffect(() => {
          if (
            pageType === "SUMMARY" &&
            summaryEditOpenedFromCoApplicantSection &&
            loanDetailCoApplicantCollapseValue
          ) {
            setExpanded(loanDetailCoApplicantCollapseValue);
          }
        }, [
          loanDetailCoApplicantCollapseValue,
          pageType,
          summaryEditOpenedFromCoApplicantSection,
        ]);

        // Close the accordion if the active co-applicant is removed.
        React.useEffect(() => {
          const isActiveSummaryCoApplicantTarget =
            pageType === "SUMMARY" &&
            summaryEditOpenedFromCoApplicantSection &&
            selectedCoApplicantKey &&
            expanded === selectedCoApplicantKey;

          if (isActiveSummaryCoApplicantTarget) {
            return;
          }

          if (
            expanded &&
            typeof expanded === "string" &&
            !coApplicantKeys.includes(expanded)
          ) {
            setExpanded(false);
            dispatch(setLoanDetailCoApplicantCollapseValue(""));
          }
        }, [
          coApplicantKeys,
          dispatch,
          expanded,
          pageType,
          selectedCoApplicantKey,
          summaryEditOpenedFromCoApplicantSection,
        ]);

        React.useEffect(() => {
          if (pageType === "SUMMARY") return;
          if (defaultExpandedInitializedRef.current) return;
          if (coApplicantKeys.length === 0) return;

          defaultExpandedInitializedRef.current = true;
          const firstCoApplicantKey = coApplicantKeys[0];
          setExpanded(firstCoApplicantKey);
          dispatch(setLoanDetailCoApplicantCollapseValue(firstCoApplicantKey));
        }, [coApplicantKeys, dispatch, pageType]);

        // const [tabIndex, setTabIndex] = React.useState<Record<string, number>>(
        //   {}
        // );
        // Track enabled tabs per co-applicant: { [coAppKey]: [true, false, false] }
        const [tabEnabled, setTabEnabled] = React.useState<
          Record<string, boolean[]>
        >({});

        // Initialize tabEnabled for each co-applicant (only first tab enabled by default)
        React.useEffect(() => {
          coApplicantKeys.forEach((coAppKey) => {
            const tabFieldsArr = [
              coApplicantFieldMap[coAppKey]?.basic ?? [],
              coApplicantFieldMap[coAppKey]?.address ?? [],
              coApplicantFieldMap[coAppKey]?.employment ?? [],
            ];
            const persistedTabStates = [0, 1, 2].map((tabIdx) => {
              const requiredFields = (tabFieldsArr[tabIdx] || []).filter((f) => {
                const employmentMatch = f.name.match(
                  /^employmentCoApplicant(\d+)/
                );
                if (employmentMatch) {
                  return isCoApplicantEmploymentFieldRequired(
                    f,
                    formikRef?.current?.values || {},
                    employmentMatch[1]
                  );
                }
                return isTruthyFormFieldRequired(f.required);
              });
	              return (
	                requiredFields.length > 0 &&
	                requiredFields.every(
	                  (f) =>
	                    !isCoApplicantFieldValueMissing(
	                      f,
	                      f.value ?? formikRef?.current?.initialValues?.[f.name]
	                    )
	                )
	              );
            });
            const isBasicSaved =
              savedBasicCoApplicants?.[coAppKey] === true || persistedTabStates[0];
            const isAddressSaved =
              savedAddressCoApplicants?.[coAppKey] === true || persistedTabStates[1];
            const nextTabEnabled = [true, isBasicSaved, isAddressSaved];

            setTabEnabled((prev) =>
              prev[coAppKey]?.every(
                (enabled, index) => enabled === nextTabEnabled[index]
              )
                ? prev
                : { ...prev, [coAppKey]: nextTabEnabled }
            );
          });
        }, [coApplicantKeys, savedBasicCoApplicants, savedAddressCoApplicants, formikRef?.current?.values]);

        const handleAccordionChange = (panel: string) => () => {
          const nextExpanded = expanded === panel ? false : panel;
          dispatch(
            setLoanDetailCoApplicantCollapseValue(
              typeof nextExpanded === "string" ? nextExpanded : ""
            )
          );
          setExpanded(nextExpanded);
        };

        const handleZipCode = (coAppKey: any) => {
          const zipCodePermanentVal =
            formData?.[
            `addressCoApplicant${coAppKey.split("CoApplicant")?.[1]
            }permanentPincode`
            ];

          const zipCodeCurrentVal =
            formData?.[
            `addressCoApplicant${coAppKey.split("CoApplicant")?.[1]
            }currentPincode`
            ];

          // Only call API if pincode exists and is 6 digits
          if (zipCodePermanentVal && zipCodePermanentVal.length === 6) {
            handleInvokZipCode(
              zipCodePermanentVal,
              coAppKey.split("CoApplicant")?.[1],
              "permanent"
            );
          }

          if (zipCodeCurrentVal && zipCodeCurrentVal.length === 6) {
            handleInvokZipCode(
              zipCodeCurrentVal,
              coAppKey.split("CoApplicant")?.[1],
              "current"
            );
          }
        };

        const handleTabChange = (coAppKey: string, newIndex: number) => {
          const currentIndex = tabIndex[coAppKey] || 0;
          if (newIndex <= currentIndex) {
            setTabIndex((prev: any) => ({ ...prev, [coAppKey]: newIndex }));
            return;
          }
          if (!tabEnabled[coAppKey]?.[newIndex]) {
            setSnackbar({
              open: true,
              message:
                newIndex === 1
                  ? "Please save Basic Details first."
                  : "Please save Basic & Address Details first.",
              severity: "error",
            });
            return;
          }
          if (newIndex === 1) {
            handleZipCode(coAppKey);
          }
          setTabIndex((prev: any) => ({ ...prev, [coAppKey]: newIndex }));
        };
        // --- Save & Next/Save button handlers ---
        const handleTabSave = (
          coAppKey: string,
          tabIdx: number,
          isLast: boolean
        ) => {
          // Save current co-applicant's fields (all tabs) + LoanDetailsLoanAmount/collateral
          (async () => {
            
            setIsSaveNextLoading(true);
            try {
            const formData: any[] = [];
            const coAppNum = coAppKey.replace("CoApplicant", "");
 console.log(coAppNum,coAppKey, 'coAppNum')
            const values = { ...(formikRef?.current?.values || {}) };
            // --- Save: Yup + required merge for current tab (Basic / Address / Employment) ---
            const groupForSave = coApplicantFieldMap[coAppKey];
            if (groupForSave && formikRef?.current) {
              const sortTabFields = (arr: any[]) =>
                (arr || [])
                  .slice()
                  .sort(
                    (a: any, b: any) =>
                      (a?.fieldOrder ?? 0) - (b?.fieldOrder ?? 0)
                  );
              const coApplicantAddressLineFields = sortTabFields(
                groupForSave.address
              ).filter((field: any) =>
                new RegExp(
                  `^addressCoApplicant${coAppNum}(permanent|current)AddressLine\\d+$`,
                  "i"
                ).test(String(field?.name || ""))
              );
              const fieldsForValidation =
                tabIdx === 0
                  ? sortTabFields(groupForSave.basic)
                  : tabIdx === 1
                    ? sortTabFields(groupForSave.address)
                    : [
                        ...sortTabFields(groupForSave.employment),
                        ...coApplicantAddressLineFields,
                      ];

	              fieldsForValidation.forEach((field: any) => {
	                if (
	                  field?.name &&
	                  isCoApplicantFieldValueMissing(field, values[field.name]) &&
	                  field?.value !== undefined
	                ) {
	                  values[field.name] = field.value;
                }
              });

              const hIdx =
                typeof activeHorizontalIndex === "number"
                  ? activeHorizontalIndex
                  : 1;
              const vIdx =
                typeof activeVerticalIndex === "number"
                  ? activeVerticalIndex
                  : 5;

              const validationSchema = getValidationSchema(
                fieldsForValidation,
                hIdx,
                vIdx,
                notInList,
                pageType
              );

              const yupMerged: Record<string, string> = {};
              try {
                await validationSchema.validate(values, { abortEarly: false });
              } catch (caught: unknown) {
                if (caught instanceof Yup.ValidationError) {
                  const ve = caught;
                  if (ve.inner?.length) {
                    ve.inner.forEach((issue) => {
                      const p = issue.path;
                      if (!p) return;
                      if (!fieldsForValidation.some((f: any) => f.name === p)) {
                        return;
                      }
                      yupMerged[p] = issue.message;
                    });
                  } else if (ve.path) {
                    if (
                      fieldsForValidation.some((f: any) => f.name === ve.path)
                    ) {
                      yupMerged[ve.path] = ve.message;
                    }
                  }
                }
              }

              Object.keys(yupMerged).forEach((key) => {
                if (coApplicantEmploymentSaveExcluded(key, values, coAppNum)) {
                  delete yupMerged[key];
                }
              });

              fieldsForValidation.forEach((f: any) => {
                if (!f?.name) return;
                if (coApplicantEmploymentSaveExcluded(f.name, values, coAppNum)) {
                  return;
                }
                if (
                  /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|IsPanVerified)$/.test(
                    f.name
                  )
                ) {
                  return;
                }
                if (yupMerged[f.name]) return;
                if (!isCoApplicantEmploymentFieldRequired(f, values, coAppNum)) return;
                if (isCoApplicantFieldValueMissing(f, values[f.name])) {
                  yupMerged[f.name] = `${f.label || f.name} is required`;
                }
              });

              if (tabIdx === 0) {
                const pn = `basicCoApplicant${coAppNum}PhoneNumber`;
                const em = `basicCoApplicant${coAppNum}Email`;
                const phoneVal = values[pn] ?? "";
                if (String(phoneVal).replace(/\D/g, "").length !== 10) {
                  yupMerged[pn] = "Mobile number must be 10 digits";
                }
                const emailVal = values[em] ?? "";
                if (!JOURNEY_EMAIL_REGEX.test(String(emailVal))) {
                  yupMerged[em] = "Please enter a valid email address";
                }
              }

              const stepErrorKeys = Object.keys(yupMerged).filter((k) =>
                fieldsForValidation.some((f: any) => f.name === k)
              );

              if (stepErrorKeys.length > 0) {
                const prevErrors = { ...(formikRef.current.errors || {}) };
                fieldsForValidation.forEach((f: any) => {
                  if (f?.name) delete prevErrors[f.name];
                });
                stepErrorKeys.forEach((k) => {
                  prevErrors[k] = yupMerged[k];
                });
                formikRef.current.setErrors(prevErrors);
                const prevTouched = {
                  ...(formikRef.current.touched as Record<string, boolean>),
                };
                stepErrorKeys.forEach((k) => {
                  prevTouched[k] = true;
                });
                formikRef.current.setTouched(prevTouched, false);
                console.log(yupMerged, 'yupMerged')
                console.log(prevTouched, 'yupMerged')
                console.log(formikRef.current.errors, 'yupMerged')
                console.log(fieldsForValidation, 'yupMerged vali')
                setSnackbar({
                  open: true,
                  message:
                    "Some fields are missing or invalid. Update them and save again.",
                  severity: "error",
                });
                return;
              }
            }

            // Save can be clicked before the mount-time master-list load has
            // answered; wait for it so nationality / marital status / country
            // resolve to their names and ids instead of going out blank.
            const loadedMasterOptions = await loadMasterOptions();
            const saveNationalityMasterOptions = nationalityMasterOptions?.length
              ? nationalityMasterOptions
              : loadedMasterOptions.nationalityOptions;
            const saveMaritalStatusMasterOptions = maritalStatusMasterOptions?.length
              ? maritalStatusMasterOptions
              : loadedMasterOptions.maritalStatusOptions;
            const saveBirthCountryMasterOptions = birthCountryMasterOptions?.length
              ? birthCountryMasterOptions
              : loadedMasterOptions.countryOptions;
            const getCoAppOptionLabel = (suffix: string): string => {
              const fieldName = `basicCoApplicant${coAppNum}${suffix}`;
              const rawValue = values[fieldName];
              if (rawValue === null || rawValue === undefined || rawValue === "") {
                return "";
              }
              if (typeof rawValue === "object" && !Array.isArray(rawValue)) {
                return String(
                  (rawValue as any)?.label ?? (rawValue as any)?.value ?? ""
                );
              }
              const fieldDef = fields.find((f: any) => f?.name === fieldName);
              const fieldOptions = fieldDef?.options;
              let options: any[] = [];
              const normalizedRaw = String(rawValue).trim();

              if (Array.isArray(fieldOptions?.options)) {
                options = fieldOptions.options;
              } else if (Array.isArray(fieldOptions)) {
                options = fieldOptions;
              }

              // Fallback to master options for dynamically added co-applicants.
              if (!options.length) {
                if (suffix === "Nationality") {
                  options =
                    (Array.isArray(nationalityOptions?.options)
                      ? nationalityOptions.options
                      : saveNationalityMasterOptions) ?? [];
                } else if (suffix === "MaritalStatus") {
                  options = maritalStatusOptions ?? saveMaritalStatusMasterOptions ?? [];
                } else if (suffix === "CountryOfBirth") {
                  options = saveBirthCountryMasterOptions ?? [];
                }
              }

              const toOptionValue = (opt: any) =>
                String(
                  opt?.value ??
                  opt?.code ??
                  opt?.id ??
                  opt?.nationalityCode ??
                  opt?.maritalStatusCode ??
                  opt?.label ??
                  opt?.name ??
                  opt ??
                  ""
                ).trim();
              const toOptionLabel = (opt: any) =>
                String(
                  opt?.label ??
                  opt?.name ??
                  opt?.desc ??
                  opt?.nationalityDesc ??
                  opt?.maritalStatusName ??
                  opt?.maritalStatusDesc ??
                  opt?.value ??
                  opt ??
                  ""
                ).trim();

              // The saved value may be a code that is only in the master list
              // (e.g. marital status "M"), in any case: also search the master
              // list and every code key, so the full name is sent, not the code.
              const masterOptionsForLabel =
                suffix === "Nationality"
                  ? saveNationalityMasterOptions
                  : suffix === "MaritalStatus"
                    ? saveMaritalStatusMasterOptions
                    : suffix === "CountryOfBirth"
                      ? saveBirthCountryMasterOptions
                      : [];
              const rawKey = normalizedRaw.toLowerCase();
              const optionMatchesRaw = (opt: any) =>
                [
                  toOptionValue(opt),
                  toOptionLabel(opt),
                  opt?.code,
                  opt?.nationalityCode,
                  opt?.maritalStatusCode,
                  opt?.countryId,
                ].some((key) => String(key ?? "").trim().toLowerCase() === rawKey);
              const matched =
                options.find(optionMatchesRaw) ||
                (masterOptionsForLabel || []).find(optionMatchesRaw);
              return matched ? toOptionLabel(matched) : normalizedRaw;
            };

            const getCoAppOptionValue = (suffix: string): string => {
              const fieldName = `basicCoApplicant${coAppNum}${suffix}`;
              const rawValue = values[fieldName];
              const pickValueBySuffix = (source: any): string => {

                if (suffix === "CountryOfBirth") {
                  return String(
                      source?.countryId ??
                      source?.id ??
                      source?.value ??
                      source?.code ??
                      ""
                  ).trim();
                }
                if (suffix === "CityOfBirth") {
                  return String(
                    source?.cityId ??
                      source?.id ??
                      source?.value ??
                      source?.code ??
                      ""
                  ).trim();
                }
                if (suffix === "Nationality") {
                  return String(
                      source?.nationalityId ??
                      source?.nationalityCode ??  
                      source?.value ??
                      source?.code ??
                      source?.id ??
                      ""
                  ).trim();
                }
                if (suffix === "MaritalStatus") {
                  return String(
                    source?.maritalStatusCode ??
                      source?.value ??
                      source?.code ??
                      source?.id ??
                      ""
                  ).trim();
                }
                return String(
                  source?.value ??
                    source?.code ??
                    source?.id ??
                    source?.countryId ??
                    source?.cityId ??
                    source?.nationalityCode ??
                    source?.maritalStatusCode ??
                    ""
                ).trim();
              };
              if (rawValue === null || rawValue === undefined || rawValue === "") {
                return "";
              }
              if (typeof rawValue === "object" && !Array.isArray(rawValue)) {
                return pickValueBySuffix(rawValue as any);
              }
              const fieldDef = fields.find((f: any) => f?.name === fieldName);
              const fieldOptions = fieldDef?.options;
              let options: any[] = [];
              const normalizedRaw = String(rawValue).trim();

              if (Array.isArray(fieldOptions?.options)) {
                options = fieldOptions.options;
              } else if (Array.isArray(fieldOptions)) {
                options = fieldOptions;
              }

              if (!options.length) {
                if (suffix === "Nationality") {
                  options =
                    (Array.isArray(nationalityOptions?.options)
                      ? nationalityOptions.options
                      : saveNationalityMasterOptions) ?? [];
                } else if (suffix === "MaritalStatus") {
                  options = maritalStatusOptions ?? saveMaritalStatusMasterOptions ?? [];
                } else if (suffix === "CountryOfBirth") {
                  options = saveBirthCountryMasterOptions ?? [];
                }
              }

              const toOptionValue = (opt: any) => pickValueBySuffix(opt);
              const toOptionLabel = (opt: any) =>
                String(
                  opt?.label ??
                    opt?.name ??
                    opt?.desc ??
                    opt?.nationalityDesc ??
                    opt?.maritalStatusName ??
                    opt?.maritalStatusDesc ??
                    ""
                ).trim();

              const matched = options.find(
                (opt: any) =>
                  toOptionValue(opt) === normalizedRaw ||
                  toOptionLabel(opt) === normalizedRaw
              );
              return matched ? toOptionValue(matched) : normalizedRaw;
            };

            const nationalityLabel = getCoAppOptionLabel("Nationality");
            const maritalStatusLabel = getCoAppOptionLabel("MaritalStatus");
            const countryOfBirthLabel = getCoAppOptionLabel("CountryOfBirth");
            const cityOfBirthLabel = getCoAppOptionLabel("CityOfBirth");
            const maritalStatusId = getCoAppOptionValue("MaritalStatus");
            const nationalityId = getCoAppOptionValue("Nationality");
            const birthCountryId = getCoAppOptionValue("CountryOfBirth");
            const birthCityId = getCoAppOptionValue("CityOfBirth");
            const coApplicantBirthCountryForProcessio =
              values[`basicCoApplicant${coAppNum}CountryOfBirth`];
            const coApplicantBirthCityForProcessio =
              values[`basicCoApplicant${coAppNum}CityOfBirth`];
            const coApplicantBirthCountryText = normalizeCoApplicantFieldValue(
              coApplicantBirthCountryForProcessio
            );
            const coApplicantBirthCityText = normalizeCoApplicantFieldValue(
              coApplicantBirthCityForProcessio
            );
            // Cities are listed by country name; an untouched birth country can
            // hold the country code, so look its name up in the master list first
            const coApplicantBirthCountryName =
              saveBirthCountryMasterOptions?.find((country: any) =>
                [country?.label, country?.value, country?.countryId].some(
                  (countryText) =>
                    normalizeCoApplicantFieldValue(countryText).toLowerCase() ===
                    coApplicantBirthCountryText.toLowerCase()
                )
              )?.label || coApplicantBirthCountryText;
            let coApplicantBirthCityOptions =
              getCityOptionsForCountry(coApplicantBirthCountryName);
            if (
              coApplicantBirthCountryName &&
              !coApplicantBirthCityOptions.length
            ) {
              coApplicantBirthCityOptions = await fetchCitiesByCountryState(
                coApplicantBirthCountryName
              );
            }
            const birthCountryIdForProcessio =
              saveBirthCountryMasterOptions?.find((country: any) => {
                const selectedCountry =
                  coApplicantBirthCountryText.toLowerCase();
                return [
                  normalizeCoApplicantFieldValue(country?.label),
                  normalizeCoApplicantFieldValue(country?.value),
                  normalizeCoApplicantFieldValue(country?.countryId),
                ].some(
                  (countryText) =>
                    countryText.toLowerCase() === selectedCountry
                );
              })?.countryId || birthCountryId;
            const birthCityIdForProcessio =
              coApplicantBirthCityOptions?.find(
                (city: any) => coApplicantBirthCityText === city?.label
              )?.value || birthCityId;

            fields.forEach((field: any) => {
              if (field.name.includes(`CoApplicant${coAppNum}`)) {
                if (
                  !isCoApplicantEmploymentFieldVisible(
                    field.name,
                    values,
                    coAppNum
                  )
                ) {
                  return;
                }
                // Skip AadhaarNumber field
                // if (field.name === `basicCoApplicant${coAppNum}AadhaarNumber`) {
                //   return;
                // }
                let fieldValue = values[field.name] ?? "";
                if (
                  typeof fieldValue === "object" &&
                  fieldValue !== null &&
                  !Array.isArray(fieldValue)
                ) {
                  fieldValue =
                    (fieldValue as any)?.label ?? (fieldValue as any)?.value ?? "";
                }
                if (
                  field.name === `basicCoApplicant${coAppNum}Nationality` ||
                  field.name === `basicCoApplicant${coAppNum}MaritalStatus`
                ) {
                  const mappedLabel = field.name.endsWith("Nationality")
                    ? nationalityLabel
                    : maritalStatusLabel;
                  fieldValue = mappedLabel || fieldValue;
                }
                if (
                  field.name ===
                  `basicCoApplicant${coAppNum}AadhaarVerified`
                ) {
                  fieldValue =
                    fieldValue === true || fieldValue === "true";
                }
                // Designation is stored as its code; formData carries the full name.
                if (field.name === `employmentCoApplicant${coAppNum}designation`) {
                  fieldValue = getMasterOptionName(values[field.name], designationOptions);
                }
                formData.push({
                  formFieldId: field.id,
                  fieldKey: field.name,
                  fieldValue,
                });
              }
            });
            // Add extra keys for API payload
            // Find correct keys for this co-applicant
            const mobileNumber =
              values[`basicCoApplicant${coAppNum}PhoneNumber`] || "";
            const email = values[`basicCoApplicant${coAppNum}Email`] || "";
            const coApplicantType = `Co-Applicant ${coAppNum}`;

            const payload = {
                trackingId: trackingId,
                coApplicantType,
              };
              

            const coAppIndex = String(Number(coAppNum) - 1); // '0' for type 1, '1' for type 2
            const aadhaarSeedingStatus =
              coApplicantVerification?.[coAppIndex]?.aadhaarSeedingStatus;
            const isAadhaarSeeded =
              aadhaarSeedingStatus === undefined ||
                aadhaarSeedingStatus === null ||
                aadhaarSeedingStatus === ""
                ? false
                : aadhaarSeedingStatus;

            const finalIsAadhaarSeeded =
              isAadhaarSeeded === "" ? false : isAadhaarSeeded;
            try {
              const shouldAutoInvokeLeadFlow: boolean =
                pageType !== "SUMMARY" &&
                (checkIsLeadApiInvoked.ASSIGN_SM == false ||
                  checkIsLeadApiInvoked.CREATE_LEAD == false ||
                  checkIsLeadApiInvoked.LEAD_TO_APPLICATION == false);
              if (shouldAutoInvokeLeadFlow) {
                await handleLeadCreationFlow(getLeadPayload, leadSequenceId);
              }
              dispatch(updateFormData(values));
              let section="basic";
              if(tabIdx===1){
                section="address";
              }else if(tabIdx===2){
                section="employment";
              }
              // idDto's cityId / pincodeId / stateId must match the pincode being
              // saved. The lookup cache is only filled when a pincode is typed
              // (debounced), so it can be empty (untouched pincode) or still hold
              // the previous pincode (edited just before saving): look up the
              // current pincode here and use the cache only if that fails.
              const lookupZipForDto = async (pincodeValue: any, cached: any) => {
                const pincodeForDto = String(pincodeValue || "").trim();
                if (!/^\d{6}$/.test(pincodeForDto)) return cached;
                try {
                  const zipRespForDto = await eligibilityCheckServices.getZipCode(
                    pincodeForDto,
                  );
                  const stateCodeFromZipForDto =
                    zipRespForDto?.data?.data?.[0]?.stateCode;
                  const pinRespForDto = await eligibilityCheckServices.getPinCode(
                    pincodeForDto,
                  );
                  if (pinRespForDto?.data?.data?.[0]) {
                    return {
                      ...pinRespForDto.data.data[0],
                      stateCode: stateCodeFromZipForDto,
                    };
                  }
                } catch {
                  // keep the cached value; idDto's ids for this address stay blank
                }
                return cached;
              };
              const cachedZipForDto = coApplicantZipCodeData?.[coAppKey] || {};
              let companyZipForDto = cachedZipForDto?.company;
              let permanentZipForDto = cachedZipForDto?.permanent;
              let currentZipForDto = cachedZipForDto?.current;
              if (section === "employment") {
                const companyPincodeField = `employmentCoApplicant${coAppNum}pincode`;
                // No company address when not employed or Farmer / Others: don't
                // send the city / pincode / state ids of an earlier company pincode
                companyZipForDto = isCoApplicantEmploymentFieldVisible(
                  companyPincodeField,
                  values,
                  coAppNum
                )
                  ? await lookupZipForDto(values?.[companyPincodeField], companyZipForDto)
                  : null;
              } else if (section === "address") {
                permanentZipForDto = await lookupZipForDto(
                  values?.[`addressCoApplicant${coAppNum}permanentPincode`],
                  permanentZipForDto,
                );
                const currentSameAsPermanent =
                  values?.[`addressCoApplicant${coAppNum}currentsameAsPermanent`];
                currentZipForDto =
                  currentSameAsPermanent === true || currentSameAsPermanent === "true"
                    ? permanentZipForDto
                    : await lookupZipForDto(
                        values?.[`addressCoApplicant${coAppNum}currentPincode`],
                        currentZipForDto,
                      );
              }

              // idDto: resolved-ID mirror of formData above, for this
              const idDto = buildCoApplicantSaveIdDto({
                getField: (fieldSection, suffix) => {
                  switch (fieldSection) {
                    case "basic": {
                      const rawBasic = values[`basicCoApplicant${coAppNum}${suffix}`];
                      // An untouched dropdown holds the saved value, which may only
                      // match the field's own options. Send the label resolved from
                      // them too, so idDto can match the master list by name.
                      const resolvedBasicLabel =
                        suffix === "Nationality"
                          ? nationalityLabel
                          : suffix === "MaritalStatus"
                            ? maritalStatusLabel
                            : suffix === "CountryOfBirth"
                              ? countryOfBirthLabel
                              : "";
                      if (!resolvedBasicLabel) return rawBasic;
                      return {
                        value:
                          rawBasic && typeof rawBasic === "object"
                            ? rawBasic?.value ?? rawBasic?.label
                            : rawBasic,
                        label: resolvedBasicLabel,
                      };
                    }
                    case "permanent":
                      return values[
                        `addressCoApplicant${coAppNum}permanent${suffix}`
                      ];
                    case "current":
                      return values[
                        `addressCoApplicant${coAppNum}current${suffix}`
                      ];
                    // Fields hidden by "Is employed = No" or Farmer / Others keep
                    // their old values in the form, so they are sent as blank
                    case "employment":
                    case "company": {
                      const employmentFieldName =
                        fieldSection === "company"
                          ? `employmentCoApplicant${coAppNum}company${suffix}`
                          : `employmentCoApplicant${coAppNum}${suffix}`;
                      return isCoApplicantEmploymentFieldVisible(
                        employmentFieldName,
                        values,
                        coAppNum
                      )
                        ? values[employmentFieldName]
                        : "";
                    }
                    default:
                      return undefined;
                  }
                },
                stage: section as "basic" | "address" | "employment",
                // saveBirthCountryMasterOptions (useApplicantMasterData's country
                // dropdownData.countries may not be loaded on every page, so the
                // master country list is included as well
                countryOptions: [
                  ...(() => {
                    const birthCountryFieldOptions = fields?.find(
                      (f: any) =>
                        f?.name === `basicCoApplicant${coAppNum}CountryOfBirth`
                    )?.options;
                    return Array.isArray(birthCountryFieldOptions?.options)
                      ? birthCountryFieldOptions.options
                      : Array.isArray(birthCountryFieldOptions)
                        ? birthCountryFieldOptions
                        : [];
                  })(),
                  ...(dropdownData?.countries || []),
                  ...(saveBirthCountryMasterOptions || []),
                ],
                // The options the dropdowns actually use (form JSON), then the
                // master lists, so a saved value from either one is matched
                maritalStatusOptions: [
                  ...(Array.isArray(maritalStatusOptions) ? maritalStatusOptions : []),
                  ...(saveMaritalStatusMasterOptions || []),
                ],
                nationalityOptions: [
                  ...(Array.isArray(nationalityOptions?.options)
                    ? nationalityOptions.options
                    : Array.isArray(nationalityOptions)
                      ? nationalityOptions
                      : []),
                  ...(saveNationalityMasterOptions || []),
                ],
                cityOptionsForBirth: coApplicantBirthCityOptions || [],
                coApplicantZipCodeData: {
                  ...coApplicantZipCodeData,
                  [coAppKey]: {
                    ...cachedZipForDto,
                    company: companyZipForDto,
                    permanent: permanentZipForDto,
                    current: currentZipForDto,
                  },
                },
                coAppKey,
                designations: designationOptions,
              });
              let res = await postsServices.addPost(
                "api/v1/applicant/save-coapplicant",
                {
                trackingId,
                mobileNumber,
                coApplicantType,
                  email: email.toLowerCase(),
                  nationality: nationalityLabel,
                  maritalstatus: maritalStatusLabel,
                  cityOfBirth: cityOfBirthLabel,
                  countryOfBirth: countryOfBirthLabel,
                  isAadhaarSeeded:
                    isAadhaarSeeded === "" ||
                      isAadhaarSeeded === null ||
                      isAadhaarSeeded === undefined
                      ? finalIsAadhaarSeeded
                      : isAadhaarSeeded,
                formData,
                section: section,
                idDto,
                }
              );
              const responseSection =
                res?.data?.data?.section || res?.data?.section;
              const isSectionSaveSuccess =
                (res?.status === 200 || res?.status === 201) &&
                (!responseSection ||
                  String(responseSection).toLowerCase() === section);
              if (!isSectionSaveSuccess) {
                throw new Error(
                  res?.data?.message ||
                    `Failed to save co-applicant ${section} details.`
                );
              }
              let coApplicantPersist: any = null
              const resp: any = await losServices.getCoApplicantDetails(
                payload
              );
              coApplicantPersist = resp?.data?.data?.applicantId
              
              setSnackbar({
                open: true,
                message: "Co-applicant details for the tab saved successfully.",
                severity: "success",
              });
              dispatch(bumpCompletionSummaryRefreshNonce());
              // dispatch(
              //   setStatusPopup({
              //     status: "success",
              //     message:
              //       "Co-applicant details for the tab saved successfully",
              //   })
              // );
              if (tabIdx === 0 && (res?.status === 200 || res?.status === 201)) {
                dispatch(
                  setSavedBasicForCoApplicant({
                    coAppKey, // e.g. "CoApplicant1"
                    value: true,
                  })
                );
              }

              // On success, enable next tab for this co-applicant (if not last)
              if (!isLast) {
                setTabEnabled((prev) => {
                  const arr = prev[coAppKey]
                    ? [...prev[coAppKey]]
                    : [true, false, false];
                  arr[tabIdx + 1] = true;
                  return { ...prev, [coAppKey]: arr };
                });
                setTabIndex((prev: any) => ({ ...prev, [coAppKey]: tabIdx + 1 }));
              }

              if (tabIdx == 0) {
                // Call handleZipCode after saving basic details to fetch pincode data
                handleZipCode(coAppKey);
              }

              const genderValue =
                values[`basicCoApplicant${coAppNum}Gender`];
              const genderId =
                mapGender().find(
                  (item: any) =>
                    item.name ===
                    values[`basicCoApplicant${coAppNum}Gender`]
                )?.code || "";
                // extraction, its own behavior is unchanged.
              

              if (tabIdx === 0 && res?.data?.leadId) {
                if (coApplicantPersist) {
                  
                  trackCustomerJourneyEvent(
                    `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                    decrypt(userProfile?.id) ?? "",
                    trackingId,
                    {
                      label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                    }
                  );

                } else {
                  
                  if (Number(applicationId) > 0) {
                    trackCustomerJourneyEvent(
                      `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      decrypt(userProfile?.id) ?? "",
                      trackingId,
                      {
                        label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                      }
                    );
                  }
                }  
              }
              if (tabIdx === 1) {
                dispatch(
                  setSavedAddressForCoApplicant({
                    coAppKey, // e.g. "CoApplicant1"
                    value: true,
                  })
                );
                
                trackCustomerJourneyEvent(
                  `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                  decrypt(userProfile?.id) ?? "",
                  trackingId,
                  {
                    label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Address Details`,
                  }
                );

              }
              if (tabIdx === 2) {
                if (res?.status === 200 || res?.status === 201) {
                  if (typeof window !== "undefined") {
                    window.sessionStorage.setItem(
                      `coApplicantEmploymentSaved:${trackingId}:${coAppNum}`,
                      "true"
                    );
                  }
                  dispatch(bumpFormJsonRefreshNonce());
                  setExpanded(false);
                  dispatch(setLoanDetailCoApplicantCollapseValue(""));
                  dispatch(setDisableAddMoreCoApplicant(false));
                  updateAddMoreStateFromLocalValues();
                  if (pageType === "SUMMARY") {
                    dispatch(setCoApplicantSummaryViewOnly(false));
                    dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
                    dispatch(setSummaryApplicationEditActive(false));
                  }
                }
              }
            } catch (err) {
              console.error("Error saving co-applicant:", err);
            }
            } finally {
              setIsSaveNextLoading(false);
            }
          })();
        };

        return coApplicantKeys.map((coAppKey, idx) => {
          console.log(coAppKey, "coAppKey==>")
          const group = coApplicantFieldMap[coAppKey];
          const currentTab = tabIndex[coAppKey] || 0;
          const isSummaryPage = pageType === "SUMMARY";
          const coApplicantDisplayNumber = idx + 1;
          const renderSummaryHeading = (title: string) => (
            <Box
              sx={{
                backgroundColor: "#EAf7f9",
                borderLeft: "3px solid #00b3b5",
                borderRadius: "8px",
                py: 1,
                px: 2,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#4b4b4b",
                  lineHeight: "24px",
                }}
              >
                {title}
              </Typography>
            </Box>
          );
          if (
            isSummaryPage &&
            selectedCoApplicantKey &&
            selectedCoApplicantKey !== LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY &&
            coAppKey !== selectedCoApplicantKey
          ) {
            return null;
          }
          const tabLabels = [
            "Basic Details",
            "Address Details",
            "Employment Details",
          ];
          const sortByFieldOrder = (arr: any[]) =>
            (arr || []).slice().sort((a: any, b: any) => {
              const aOrder = a?.fieldOrder ?? 0;
              const bOrder = b?.fieldOrder ?? 0;
              return aOrder - bOrder;
            });
          const tabFields = [
            sortByFieldOrder(group.basic),
            sortByFieldOrder(group.address),
            sortByFieldOrder(group.employment),
          ];
          const coApplicantContent = (
            <>

                  {/* Note: Only for co-applicant 1 */}
                  {idx === 0 && !isSummaryPage && (
                    <Grid
                      item
                      xs={12}
                      sx={{ mb: 1, p: 1 }}
                      style={{
                        backgroundColor: "#EBF9F9",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 400,
                          fontSize: "15px",
                          color: "#5E596C",
                        }}
                      >
                        Please add the co-applicant whose financial to be
                        consider for loan evaluation
                      </Typography>
                    </Grid>
                  )}
                  {/* Tabs for Basic, Address, Employment */}
                  <Tabs
                    value={currentTab}
                    onChange={(_e: any, newIndex: any) =>
                      handleTabChange(coAppKey, newIndex)
                    }
                    aria-label={`Tabs for ${coAppKey}`}
                    variant={isXsOrSm ? "scrollable" : "standard"}
                    {...(isXsOrSm && {
                      scrollButtons: "auto",
                      allowScrollButtonsMobile: false,
                    })}
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#00b3b5",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#616161",
                        "&.Mui-selected": {
                          color: "#00b3b5",
                        },
                      },
                    }}
                  >
                    {tabLabels.map((label, i) => (
                      <Tab
                        key={label}
                        label={label}
                        disabled={isSummaryPage ? false : !tabEnabled[coAppKey]?.[i]}
                      />
                    ))}
                  </Tabs>
                  <Box sx={{ mt: 2 }}>
                    {tabFields[currentTab].length === 0 ? (
                      <Typography
                        sx={{ mt: 2 }}
                        variant="body2"
                        color="text.secondary"
                      >
                        No fields
                      </Typography>
                    ) : (
                      <>
                        {/* Aadhaar Verification block for Basic Details tab */}
                        {tabLabels[currentTab] === "Basic Details" ? (
                          <>
                            {isSummaryPage && renderSummaryHeading("Basic Details")}
                            {/* Render all basic fields first */}
                            <Grid container spacing={2}>
                              {tabFields[currentTab]
                                .filter((field: any) => {
                                  // Exclude AadhaarNumber and all IsPanVerified, IsMobileVerified, IsEmailVerified, AadhaarVerified fields for any co-applicant
                                  // Also exclude basicCoApplicant1PayParty for co-applicant 1 only
                                  if (
                                    /^basicCoApplicant\d+AadhaarNumber$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsPanVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsMobileVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsEmailVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+AadhaarVerified$/.test(
                                      field.name
                                    )
                                  ) {
                                    return false;
                                  }
                                  // Exclude PayParty for co-applicant 1 only
                                  if (
                                    field.name === "basicCoApplicant1PayParty"
                                  ) {
                                    return false;
                                  }
                                  const otherRelationMatch = field.name.match(
                                    /^basicCoApplicant(\d+)OtherRelationWithStudent$/i
                                  );
                                  if (otherRelationMatch) {
                                    const relationFieldName =
                                      `basicCoApplicant${otherRelationMatch[1]}RelationWithStudent`;
                                    return (
                                      isOtherRelationshipSelected(
                                        currentFormValues[relationFieldName]
                                      ) ||
                                      isOtherRelationshipSelected(
                                        currentFormValues[
                                          `${relationFieldName}Name`
                                        ]
                                      )
                                    );
                                  }
                                  return true;
                                })
                                ?.sort((prev: any, next: any) => prev?.fieldOrder - next?.fieldOrder)
                                ?.map((field: any) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={
                                      field.name.includes("PayParty") ? 12 : 6
                                    }
                                    md={
                                      field.name.includes("PayParty") ? 12 : 4
                                    }
                                    key={field.name}
                                  >
                                    {renderField(field)}
                                  </Grid>
                                ))}
                            </Grid>
                            {/* Aadhaar Verification UI */}
                            {pageType !== "SUMMARY" && (
                            <Grid container className="inner-form-box" mt={2}>
                              <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <img
                                    src={idIcon}
                                    alt="icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: "#4B465C",
                                    }}
                                  >
                                    Verify Aadhaar Number
                                  </Typography>
                                </Box>
                                <label
                                  className="text-lable"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Verify your Aadhaar to enhance processing speed and accuracy. This streamlined step is provided for your ease and remains optional, allowing you to enter details manually if you prefer.
                                </label>
                              </Grid>
                              {isAadharLoading[coAppKey] ? (
                                <Skeleton
                                  variant="rectangular"
                                  width={120}
                                  height={40}
                                  sx={{ borderRadius: 2, mb: 1 }}
                                />
                              ) : (
                                <IconButton
                                  sx={{
                                    backgroundColor:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : undefined,
                                    color:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : "#000",
                                    border:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "1px solid #33c2c4"
                                        : "",
                                    "&.Mui-disabled": {
                                      backgroundColor: formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "white !important"
                                        : "lightgray !important",
                                      color:
                                        formikRef?.current?.values?.[
                                          `basicCoApplicant${coAppKey.replace(
                                            "CoApplicant",
                                            ""
                                          )}AadhaarVerified`
                                        ] === true ||
                                          isDocumentVerifiedCoApplicant[coAppKey]
                                          ? "#33c2c4"
                                          : "#000",
                                    },
                                  }}
                                  onClick={async () => {
                                    // Find co-applicant values for this tab
                                    const coAppNum = coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    );
                                    const values =
                                      formikRef?.current?.values || {};
                                    const getField = (suffix: string) =>
                                      values[
                                      `basicCoApplicant${coAppNum}${suffix}`
                                      ] || "";
                                    const email = getField("Email");
                                    const firstName = getField("FirstName");
                                    const lastName = getField("LastName");
                                    const phone_number =
                                      getField("PhoneNumber");
                                    setIsAadharLoading((prev) => ({
                                      ...prev,
                                      [coAppKey]: true,
                                    }));
                                    try {

                                      trackCustomerJourneyEvent(
                                        `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                        decrypt(userProfile?.id) ?? "",
                                        trackingId,
                                        {
                                          label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                          category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                          action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                          page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Aadhaar Verification`,
                                        }
                                      );

                                      const panNumber = getField("PanNumber");
                                      const aadhaarPayload: any = {
                                        applicantEmail: email?.toLowerCase(),
                                        applicantName: `${firstName || ""} ${lastName || ""
                                          }`.trim(),
                                        applicantMobile: phone_number,
                                        applicantPan: panNumber,
                                        redirectUrl: `${window.location.origin}/assisted-portal`,
                                        landingPageNotRequired: true,
                                        trackingId: trackingId,
                                        typeOfApplicant: `Co-Applicant ${coAppNum}`,
                                      };

                                      const resp = await postsServices.addPost(
                                        "api/v1/applicant/create-app-via-aadhaar-phone",
                                        aadhaarPayload
                                      );

                                      if (
                                        resp.data?.status === "success" &&
                                        resp.data.data?.link
                                      ) {


                                        let link = resp.data.data.link;
                                        const applicationNo =
                                          resp.data.data.applicationNo;
                                        localStorage.setItem(
                                          "applicationNo",
                                          applicationNo
                                        );
                                        localStorage.setItem(
                                          "aadhaarCoAppKey",
                                          coAppKey
                                        );
                                        dispatch(
                                          setAadhaarApplicationNoCoApplicant({
                                            coAppKey,
                                            applicationNo,
                                          })
                                        );
                                        // Optionally: dispatch(setAadhaarApplicationNo(applicationNo));
                                        if (!/^https?:\/\//i.test(link)) {
                                          link = "https://" + link;
                                        }
                                        window.location.replace(link);
                                      } else {
                                        setSnackbar({
                                          open: true,
                                            message: resp.data?.message,
                                          severity: "success",
                                        });
                                        // dispatch(
                                        //   setStatusPopup({
                                        //     status: "info",
                                        //     message: resp.data?.message,
                                        //   })
                                        // );
                                      }
                                    } catch (err: any) {
                                      setSnackbar({
                                        open: true,
                                          message: err?.response?.data?.message,
                                        severity: "error",
                                      });
                                      // dispatch(
                                      //   setStatusPopup({
                                      //     status: "info",
                                      //     message: err?.response?.data?.message,
                                      //   })
                                      // );
                                    } finally {
                                      setIsAadharLoading((prev) => ({
                                        ...prev,
                                        [coAppKey]: false,
                                      }));
                                    }
                                  }}
                                  disabled={(() => {
                                    const coAppNum = coAppKey.replace("CoApplicant", "");
                                    // Already verified — disable
                                    const isAadhaarVerified =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}AadhaarVerified`] === true ||
                                      isDocumentVerifiedCoApplicant[coAppKey];
                                    // Mobile must be exactly 10 digits (new validation)
                                    const mobileVal: string =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}PhoneNumber`] ?? "";
                                    const isMobileInvalid = mobileVal.replace(/\D/g, "").length !== 10;
                                    // Email must be valid format (new validation)
                                    const emailVal: string =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}Email`] ?? "";
                                    const emailRegex = JOURNEY_EMAIL_REGEX;
                                    const isEmailInvalid = !emailRegex.test(emailVal);
                                    return isAadhaarVerified || isMobileInvalid || isEmailInvalid;
                                  })()}
                                >
                                  {formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    )}AadhaarVerified`
                                    ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey] ? (
                                    <img
                                      src={VerifiedIcon}
                                      alt="arrow-narrow-right"
                                    />
                                  ) : (
                                    <></>
                                  )}

                                  {`${formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    )}AadhaarVerified`
                                  ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey]
                                    ? "Verified"
                                    : "Verify Via Digilocker"
                                    }`}

                                  {!(
                                    formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace("CoApplicant", "")}AadhaarVerified`
                                    ] === true || isDocumentVerifiedCoApplicant[coAppKey]
                                  ) && <img src={RightBlackArrow} alt="right-arrow" />
                                  }
                                </IconButton>
                              )}
                            </Grid>
                            )}
                          </>
                        ) : (
                          <Grid container spacing={2}>
                            {tabLabels[currentTab] === "Address Details"
                              ? (() => {
                                const sameAsIdx = tabFields[
                                  currentTab
                                ].findIndex((f: any) =>
                                  f.name.match(
                                    /^addressCoApplicant(\d+)currentsameAsPermanent$/
                                  )
                                );


                                const permanentFields =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab].slice(
                                      0,
                                      sameAsIdx
                                    )
                                    : tabFields[currentTab];

                                const currentFields =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab].slice(
                                      sameAsIdx + 1
                                    )
                                    : [];
                                const sameAsField =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab][sameAsIdx]
                                    : null;
                                

                                return (
                                  <>
                                    {permanentFields.length > 0 && (
                                      <Grid item xs={12}>
                                        {isSummaryPage ? (
                                          renderSummaryHeading("Permanent Address")
                                        ) : (
                                        <SectionHeader
                                          icon={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 40,
                                                height: 40,
                                                borderRadius: "8px",
                                                bgcolor: "#e5feffff",
                                              }}
                                            >
                                              <img
                                                src={locationIcon}
                                                alt="icon"
                                                style={{
                                                  width: 28,
                                                  height: 28,
                                                }}
                                              />
                                            </Box>
                                          }
                                          title={"Permanent Address"}
                                          subtitle={""}
                                        />
                                        )}
                                      </Grid>
                                    )}
                                    {permanentFields
                                      ?.filter(
                                        (field: any) =>
                                          !isCoApplicantSameAsAddressField(field)
                                      )
                                      ?.sort(
                                        (prev: any, next: any) =>
                                          prev?.fieldOrder - next?.fieldOrder,
                                      )
                                      ?.map((field: any) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={field.name}
                                      >
                                        {renderField(field)}
                                      </Grid>
                                    ))}
                                    {sameAsField && (
                                      <Grid
                                        item
                                        xs={12}
                                        key={sameAsField.name}
                                      >
                                        {renderField(sameAsField)}
                                      </Grid>
                                    )}
                                    {currentFields.length > 0 && (
                                      <Grid item xs={12}>
                                        {isSummaryPage ? (
                                          renderSummaryHeading("Current Address")
                                        ) : (
                                        <SectionHeader
                                          icon={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 40,
                                                height: 40,
                                                borderRadius: "8px",
                                                bgcolor: "#e5feffff",
                                              }}
                                            >
                                              <img
                                                src={locationIcon}
                                                alt="icon"
                                                style={{
                                                  width: 28,
                                                  height: 28,
                                                }}
                                              />
                                            </Box>
                                          }
                                          title={"Current Address"}
                                          subtitle={""}
                                        />
                                        )}
                                      </Grid>
                                    )}
                                    {currentFields
                                      ?.filter(
                                        (field: any) =>
                                          !isCoApplicantSameAsAddressField(field)
                                      )
                                      ?.sort(
                                        (prev: any, next: any) =>
                                          prev?.fieldOrder - next?.fieldOrder,
                                      )
                                      ?.map((field: any) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={field.name}
                                      >
                                        {renderField(field)}
                                      </Grid>
                                    ))}
                                  </>
                                );
                              })()
                              : tabLabels[currentTab] ===
                              "Employment Details" &&
                              (() => {
                                const employmentFields = tabFields[
                                  currentTab
                                ]
                                const isEmployedField = employmentFields.find(
                                  (f: any) =>
                                    f.type === "radio" &&
                                    /employmentCoApplicant\d+isEmployed/.test(
                                      f.name
                                    )
                                );
                                const isEmployedValue = normalizeCoApplicantFieldValue(
                                  isEmployedField
                                    ? currentFormValues[isEmployedField.name] ??
                                        isEmployedField?.value ??
                                        "Yes"
                                    : "Yes"
                                ).toLowerCase();
	                                const coAppNum =
	                                  isEmployedField?.name?.match(
	                                    /^employmentCoApplicant(\d+)isEmployed$/
	                                  )?.[1] || "";
	                                const isFirstCoApplicantEmployment =
	                                  coAppNum === "1";
                                const employmentTypeField = employmentFields.find(
                                  (f: any) =>
                                    f.name ===
                                    `employmentCoApplicant${coAppNum}employmentType`
                                );
                                const employmentVisibilityValues = {
                                  ...currentFormValues,
                                  ...(isEmployedField
                                    ? {
                                        [isEmployedField.name]:
                                          currentFormValues[
                                            isEmployedField.name
                                          ] ??
                                          isEmployedField?.value ??
                                          "Yes",
                                      }
                                    : {}),
                                  ...(employmentTypeField
                                    ? {
                                        [employmentTypeField.name]:
                                          currentFormValues[
                                            employmentTypeField.name
                                          ] ?? employmentTypeField?.value,
                                      }
                                    : {}),
                                };
                                const visibleEmploymentFields = employmentFields
                                  ?.filter(
                                    (f: any) =>
                                      !f.name.match(
                                        /employmentCoApplicant\d+isEmployed/
                                      ) &&
                                      isCoApplicantEmploymentFieldVisible(
                                        f.name,
                                        employmentVisibilityValues,
                                        coAppNum
                                      )
                                  )
                                  .sort(
                                    (prev: any, next: any) =>
                                      prev?.fieldOrder - next?.fieldOrder,
                                  )
                                  .map((field: any) =>
                                    withCoApplicantEmploymentRequiredState(
                                      field,
                                      employmentVisibilityValues,
                                      coAppNum
                                    )
                                  );
                                const isCompanyAddressField = (fieldName: string) =>
                                  /companyAddress|landmark|pincode|city|state|companyCountry/i.test(
                                    fieldName
                                  );
                                const employmentDetailFields =
                                  visibleEmploymentFields?.filter(
                                    (field: any) =>
                                      !isCompanyAddressField(field.name)
                                  ) || [];
                                const companyAddressFields =
                                  visibleEmploymentFields?.filter((field: any) =>
                                    isCompanyAddressField(field.name)
                                  ) || [];
                                const workExpYearField = employmentDetailFields.find(
                                  (field: any) =>
                                    /^employmentCoApplicant\d+employmentYear$/i.test(field.name)
                                );
                                const workExpMonthField = employmentDetailFields.find(
                                  (field: any) =>
                                    /^employmentCoApplicant\d+employmentMonth$/i.test(field.name)
                                );
                                const renderWorkExperienceFields = () => {
                                  if (!workExpYearField || !workExpMonthField) return null;
                                  return (
                                    <Grid item xs={12} key={`${workExpYearField.name}-work-exp`}>
                                      <Box sx={{ bgcolor: "#EAF7F9", p: 1.5, borderRadius: "6px" }}>
                                        <Box mb={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Box
                                            sx={{
                                              width: 18,
                                              height: 18,
                                              borderRadius: "4px",
                                              bgcolor: "#00B3B51A",
                                              color: "#00B3B5",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <WorkOutlineIcon sx={{ fontSize: 14 }} />
                                          </Box>
                                          <Typography sx={{ color: "#2F2A3C", fontSize: "13px", fontWeight: 700 }}>
                                            Help us with your work experience <span style={{ color: "red" }}>*</span>
                                          </Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} sm={6}>
                                            {renderField({
                                              ...workExpYearField,
                                              label: "",
                                              placeholder: "Total Years",
                                              required: false,
                                            })}
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            {renderField({
                                              ...workExpMonthField,
                                              label: "",
                                              placeholder: "Total Months",
                                              required: false,
                                            })}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </Grid>
                                  );
                                };
                                const renderEmploymentFieldGrid = (field: any) => {
                                  if (/^employmentCoApplicant\d+employmentMonth$/i.test(field.name)) {
                                    return null;
                                  }
                                  if (/^employmentCoApplicant\d+employmentYear$/i.test(field.name)) {
                                    return renderWorkExperienceFields();
                                  }
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={field.name}
                                    >
                                      {renderField(field)}
                                    </Grid>
                                  );
                                };
                                return (
                                  <>
	                                    {isEmployedField && (
	                                      <Grid
	                                        item
	                                        xs={12}
	                                        key={isEmployedField.name}
	                                      >
	                                        {isFirstCoApplicantEmployment && (
	                                          <Box
	                                            sx={{
	                                              backgroundColor: "#fff8e1",
	                                              border: "1px solid #ffb74d",
	                                              borderRadius: "6px",
	                                              color: "#6f4e00",
	                                              px: 2,
	                                              py: 1.2,
	                                              mb: 2,
	                                            }}
	                                          >
	                                            <Typography
	                                              sx={{
	                                                fontSize: "13px",
	                                                fontWeight: 600,
	                                              }}
	                                            >
	                                              Enter details of co-applicant whose income to be considered
	                                            </Typography>
	                                          </Box>
	                                        )}
	                                        {renderField({
	                                          ...isEmployedField,
	                                          value: isFirstCoApplicantEmployment
	                                            ? "Yes"
	                                            : isEmployedField.value,
	                                          disabled:
	                                            isFirstCoApplicantEmployment ||
	                                            isEmployedField.disabled,
	                                        })}
	                                      </Grid>
	                                    )}
                                    {isEmployedValue === "yes" &&
                                      employmentDetailFields.length > 0 && (
                                        <>
                                          <Grid item xs={12}>
                                            {isSummaryPage ? (
                                              renderSummaryHeading("Employment Details")
                                            ) : (
                                              <SectionHeader
                                                icon={
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      width: 40,
                                                      height: 40,
                                                      borderRadius: "8px",
                                                      bgcolor: "#e5feffff",
                                                    }}
                                                  >
                                                    <img
                                                      src={idIcon}
                                                      alt="icon"
                                                      style={{
                                                        width: 28,
                                                        height: 28,
                                                      }}
                                                    />
                                                  </Box>
                                                }
                                                title="Employment Details"
                                                subtitle=""
                                              />
                                            )}
                                          </Grid>
                                          {employmentDetailFields.map(
                                            renderEmploymentFieldGrid
                                          )}
                                        </>
                                      )}
                                    {isEmployedValue === "yes" &&
                                      companyAddressFields.length > 0 && (
                                        <>
                                          <Grid item xs={12}>
                                            {isSummaryPage ? (
                                              renderSummaryHeading("Company Address")
                                            ) : (
                                              <SectionHeader
                                                icon={
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      width: 40,
                                                      height: 40,
                                                      borderRadius: "8px",
                                                      bgcolor: "#e5feffff",
                                                    }}
                                                  >
                                                    <img
                                                      src={locationIcon}
                                                      alt="icon"
                                                      style={{
                                                        width: 28,
                                                        height: 28,
                                                      }}
                                                    />
                                                  </Box>
                                                }
                                                title="Company Address"
                                                subtitle=""
                                              />
                                            )}
                                          </Grid>
                                          {companyAddressFields.map(
                                            renderEmploymentFieldGrid
                                          )}
                                        </>
                                      )}
                                  </>
                                );
                              })()}
                          </Grid>
                        )}
                      </>
                    )}
                  </Box>
                  {/* Save & Next/Save button for tab navigation */}
              {!isSummaryPage && (
                    <Grid item xs={12} sx={{ mt: 2, textAlign: "left" }}>
                      <PrimaryButton
                        type="button"
                        sx={{ px: 6, minHeight: "39px" }}
                        disabled={(() => {
                          // Special logic for employmentCoApplicant2isEmployed
                          const coAppNum = coAppKey.replace("CoApplicant", "");
                          const isEmployedFieldName = `employmentCoApplicant${coAppNum}isEmployed`;
                          const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                          const isEmployed =
                            formikRef?.current?.values?.[isEmployedFieldName];
                          // Filter required fields, but if isEmployed is 'No', skip employmentType as required
                        // Also always exclude verification flag fields (AadhaarVerified, IsPanVerified, IsMobileVerified, IsEmailVerified)
                          const requiredFields = tabFields[currentTab].filter(
                            (f: any) => {
                              if (
                                !isCoApplicantEmploymentFieldVisible(
                                  f.name,
                                  formikRef?.current?.values || {},
                                  coAppNum
                                )
                              ) {
                                return false;
                              }
                            if (
                              /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|IsPanVerified)$/.test(
                                f.name
                              )
                            ) {
                              return false;
                            }
                              return isCoApplicantEmploymentFieldRequired(
                                f,
                                formikRef?.current?.values || {},
                                coAppNum
                              );
                            }
                          );
                          const hasEmptyRequired = requiredFields.some(
                            (f: any) => {
                              const val = formikRef?.current?.values?.[f.name];
                              return (
                                val === undefined || val === null || val === ""
                              );
                            }
                          );
                        // For Basic Details tab, require a 10-digit mobile number
                        const mobileValue: string =
                            formikRef?.current?.values?.[
                          `basicCoApplicant${coAppNum}PhoneNumber`
                          ] ?? "";
                        const isMobileIncomplete =
                          tabLabels[currentTab] === "Basic Details" &&
                          mobileValue.replace(/\D/g, "").length !== 10;

                        // For Basic Details tab, require a valid email address
                        const emailValue: string =
                          formikRef?.current?.values?.[
                          `basicCoApplicant${coAppNum}Email`
                          ] ?? "";
                        const emailRegex = JOURNEY_EMAIL_REGEX;
                        const isEmailInvalid =
                          tabLabels[currentTab] === "Basic Details" &&
                          !emailRegex.test(emailValue);

                          return (
                            hasEmptyRequired ||
                          isMobileIncomplete ||
                          isEmailInvalid ||
                          zipCodeLoading ||
                          isSaveNextLoading
                          );
                        })()}
                      onClick={() => {
                          handleTabSave(
                            coAppKey,
                            currentTab,
                            currentTab === tabLabels.length - 1
                        );
                        // setting empty data on button click
                        dispatch(
                          setVerificationMessage({
                            index: "",
                            applicantType: "",
                            field: "",
                            status: "",
                            message: "",
                          })
                        );
                      }}
                    >
                      {isSaveNextLoading ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : currentTab === tabLabels.length - 1 ? (
                        "Save"
                      ) : (
                        "Save & Next"
                      )}
                      </PrimaryButton>
                    </Grid>
              )}
            </>
          );
          console.log(coApplicantContent, 'coApplicantContent');
          return (
            <>
              <Grid xs={12} sx={{ pl: 2, mt: 3 }} className="border-none">
                {isSummaryPage ? (
                  <Box
                    sx={{
                      border: "1px solid #d6eef1",
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.2,
                        backgroundColor: "#eaf7f9",
                        borderBottom: "1px solid #d6eef1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#354052",
                        }}
                      >
                        {`Co-Applicant ${coApplicantDisplayNumber}`}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, ml: "auto", flexShrink: 0 }}>
                        <PrimaryButton
                          type="button"
                          disabled={isSaveNextLoading}
                          sx={summaryActionButtonStyles.save}
                          onClick={() =>
                            handleTabSave(
                              coAppKey,
                              currentTab,
                              currentTab === tabLabels.length - 1
                            )
                          }
                        >
                          {isSaveNextLoading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                          ) : (
                            "Save"
                          )}
                        </PrimaryButton>
                        <Button
                          type="button"
                          variant="outlined"
                          disabled={isSaveNextLoading}
                          onClick={handleCancelSummaryEdit}
                          sx={summaryActionButtonStyles.cancel}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>{coApplicantContent}</Box>
                  </Box>
                ) : (
                  <CollapsibleSection
                    key={coAppKey}
                    title={`Co-Applicant ${coApplicantDisplayNumber}`}
                    isExpanded={expanded === coAppKey}
                    onToggle={handleAccordionChange(coAppKey)}
                    paddingTopTabZero={true}
                  >
                    {coApplicantContent}
                  </CollapsibleSection>
                )}

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Show Remove only for extraCoApplicant (if any field isRemoved) */}
                {group.basic
                  .concat(group.address, group.employment)
                    .some((field: any) => field.isRemoved) ? (
                    <Grid sx={{ textAlign: "left", mt: 2 }}>
                      <Box
                        sx={{ display: "inline-flex", cursor: "pointer" }}
                        onClick={() => handleRemoveClick(group.ids)}
                      >
                        <img src={RemoveIcon} alt="arrow-narrow-right" />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "#FF4C51",
                            fontSize: "13px",
                          }}
                        >
                          Remove
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    <Box></Box>
                  )}
              </Grid>
              </Grid>
            </>
          );
        });
      })()}

      {pageType !== "SUMMARY" && (
      <Grid container justifyContent="end" sx={{ mx: 2, mt: 4 }}>
        {!isXsOrSm && numberOfCoApp < 9 && (
          <Grid textAlign="right">
            <Button
              sx={{
                textTransform: "none",
                fontFamily: "Jost, sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#33c2c4",
                borderColor: "#33c2c4",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "#33c2c4",
                  color: "#33c2c4",
                },
              }}
              onClick={() =>
                addCoApplicantFields({
                  source: "add-more",
                })
              }
              disabled={isLoading || disableAddMoreCoApplicant}
            >
              <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
              Add More
            </Button>
          </Grid>
        )}
      </Grid>
      )}

      {/* Proceed Button */}
      {pageType !== "SUMMARY" && (
      <Grid container justifyContent="start" sx={{ mx: 2, mt: 4, marginRight: 0 }}>
          <PrimaryButton
          sx={{ px: 6, }}
          fullWidth={isXsOrSm}
            disabled={(() => {
              // Disable if any required field is empty, but if employmentCoApplicantNisEmployed is 'No', skip employmentCoApplicantNemploymentType as required
              const values = formikRef?.current?.values || {};
              const requiredFields = fields.filter((field: any) => {
                const employmentMatch = field.name.match(
                  /^employmentCoApplicant(\d+)/
                );
                if (
                  employmentMatch &&
                  !isCoApplicantEmploymentFieldVisible(
                    field.name,
                    values,
                    employmentMatch[1]
                  )
                ) {
                  return false;
                }
                if (employmentMatch) {
                  return isCoApplicantEmploymentFieldRequired(
                    field,
                    values,
                    employmentMatch[1]
                  );
                }
                // Check for employmentType fields
              if (
                /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|AadhaarNumber)$/.test(
                  field.name
                )
              ) {
                return false;
              }
                const empTypeMatch = field.name.match(
                  /^employmentCoApplicant(\d+)employmentType$/
                );
                if (empTypeMatch) {
                  const coAppNum = empTypeMatch[1];
                  const isEmployed =
                    values[`employmentCoApplicant${coAppNum}isEmployed`];
                  if (
                    normalizeCoApplicantFieldValue(isEmployed).toLowerCase() ===
                    "no"
                  ) {
                    return false; // skip as required
                  }
                }
                return (
                  (/CoApplicant\d+/.test(field.name) ||
                    field.name === "LoanDetailsLoanAmount" ||
                    field.name === "collateral") &&
                  field.required !== false
                );
              });

              return requiredFields.some((f: any) => {
                const val = values[f.name];
                return val === undefined || val === null || val === "";
              });
            })()}
          onClick={() => handleProceedClick()}
          >
            Proceed for Provisional Offer
          </PrimaryButton>
        </Grid>
      )}

      <LoaderBackdrop
        open={
          Object.values(isAadharLoading).some(Boolean) || isSaveNextLoading
        }
      />

      {pageType !== "SUMMARY" && numberOfCoApp < 9 && (
        <Grid item xs={12}>
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              border: "1px solid #33c2c4",
              textAlign: "center",
              width: "100%",
            }}
            onClick={() =>
              addCoApplicantFields({
                source: "add-more",
              })
            }
            disabled={isLoading || disableAddMoreCoApplicant}
          >
            <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            Add Co-Applicant
          </Button>
        </Grid>
      )}

      {/* Confirmation Modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle="Remove Co-Applicant"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this Co-Applicant? This action cannot be
            undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>

      {/* Alert  */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CoApplicantDetailsSection;
